import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlertCircle, Calendar, CheckSquare, ChevronRight, ChevronLeft,
  GitBranch, Layers, Leaf, RefreshCw, X, Save, ImageIcon, Search,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { buildApiUrl } from '../../utils/apiUrl';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import type { EmpFinca, EmpLote, EmpBloque, CicloBloque } from '../../types/empacadora';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary,
  pageTitleCls, pageSubtitleCls,
} from '../../components/ui';

// ─── Constantes ───────────────────────────────────────────────────────────────

// Estadios reales (estadio_vfp) — el modelo viejo estado_ciclo (preparacion/forza/...)
// quedó obsoleto. El dropdown de edición ahora usa app_estadios (DPC/FPC/DRC/FRC/...).

// Mapeo de código VFP (estadio_vfp) → color visual. Coincide con los chips de filtro.
// Fallback: si el código no está mapeado, usa tono neutro.
const VFP_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  PRE: { bg: 'var(--surface-deep)',     border: 'var(--line)',                text: 'var(--ink-faint)' },        // Preparación
  SPC: { bg: 'var(--tone-info-bg)',     border: 'var(--tone-info-border)',    text: 'var(--tone-info-text)' },   // Semillero PC
  DPC: { bg: 'var(--tone-success-bg)',  border: 'var(--tone-success-border)', text: 'var(--tone-success-text)' },// Desarrollo PC
  FPC: { bg: 'var(--tone-warn-bg)',     border: 'var(--tone-warn-border)',    text: 'var(--tone-warn-text)' },   // Post Forza PC
  DRC: { bg: 'var(--tone-success-bg)',  border: 'var(--tone-success-border)', text: 'var(--tone-success-text)' },// Desarrollo RC
  FRC: { bg: 'var(--tone-warn-bg)',     border: 'var(--tone-warn-border)',    text: 'var(--tone-warn-text)' },   // Post Forza RC
  BAR: { bg: 'var(--surface-raised)',   border: 'var(--line)',                text: 'var(--ink-faint)' },        // Barbecho
};

const VFP_COLOR_FALLBACK = { bg: 'var(--surface-raised)', border: 'var(--line)', text: 'var(--ink-faint)' };

// Estadios "Post Forza": los únicos donde el Grupo de Forza tiene sentido.
// Fuera de ellos el dato no se muestra ni se edita (queda en BD, no se borra).
const ESTADIOS_CON_GRUPO_FORZA = new Set(['FPC', 'FRC']);

export function tieneGrupoForza(estadio?: string | null): boolean {
  return ESTADIOS_CON_GRUPO_FORZA.has((estadio ?? '').toUpperCase());
}

// Fecha-ancla de cada estadio (app_estadios.varfecha). Al pasar a uno de estos
// estadios, la fecha del cambio se copia a su ancla: de ahí salen los días del
// bloque y la programación de boletas del plan de manejo.
//
// ⚠️ Solo estadios de TRANSICIÓN. BAR/PRE comparten fecha_siembra con DPC:
// escribirla al pasar a ellos destruiría la siembra real del bloque, así que
// ahí el ancla se sigue tecleando a mano. Semillero va aparte: ver anclaDelCambio.
const ESTADIO_ANCLA_AUTO: Record<string, keyof EmpBloque & string> = {
  DPC: 'fecha_siembra',
  FPC: 'fecha_induccion',
  DRC: 'fecha_segunda',
  FRC: 'fecha_induccion2',
  MAD: 'fecha_maduracion',
};

// SPC/SRC/S3ras comparten fecha_semillero. Entrar a Semillero desde otro estadio
// sí es una fase nueva y su fecha debe quedar (sin esto, "Inicio fase" mostraba
// un dato viejo: caso Lote 11). Moverse entre semilleros no reinicia la fase.
// La fecha anterior queda en el historial del cambio (ancla_anterior).
const ESTADIOS_SEMILLERO = new Set(['SPC', 'SRC', 'S3RAS']);

function anclaDelEstadio(estadio: string): (keyof EmpBloque & string) | undefined {
  return ESTADIOS_SEMILLERO.has(estadio) ? 'fecha_semillero' : ESTADIO_ANCLA_AUTO[estadio];
}

function anclaDelCambio(nuevoEstadio: string, estadioActual?: string | null): (keyof EmpBloque & string) | undefined {
  const actual = (estadioActual ?? '').toUpperCase();
  if (ESTADIOS_SEMILLERO.has(nuevoEstadio) && ESTADIOS_SEMILLERO.has(actual)) return undefined;
  return anclaDelEstadio(nuevoEstadio);
}

// Catálogo de estadios tal como vive en app_estadios. `varfecha` es la columna
// de emp_bloques que ancla ese estadio: la fecha en que el bloque entró a la fase.
export interface EstadioCfg {
  codigo: string;
  descripcion: string;
  varfecha?: string | null;
}

// Fecha de inicio de la fase actual del bloque = su ancla según el catálogo.
// Se resuelve contra app_estadios (fuente de verdad) y cae al mapa fijo si el
// catálogo no trae varfecha para ese código.
function fechaInicioFase(b: EmpBloque, cfg: EstadioCfg[]): string | null {
  const cod = (b.estadio_vfp ?? '').toUpperCase();
  if (!cod) return null;
  const col = cfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
  if (!col) return null;
  return ((b as Record<string, any>)[col] as string | null) ?? null;
}

// Igual que fechaInicioFase, pero para un código de estadio arbitrario (no
// necesariamente el actual del bloque) — sirve para mostrar, en una fila del
// historial, cuándo entró de verdad el bloque al "De" y al "A" de ese cambio.
// Ojo: como varios estadios comparten columna (SPC/SRC/S3RAS → fecha_semillero,
// BAR/PRE/DPC → fecha_siembra), esto puede coincidir entre "De" y "A" — y esa
// coincidencia es justamente la señal útil (revela que ese cambio no movió
// ningún ancla real).
function fechaDeEstadoCodigo(b: EmpBloque, codigo: string | null | undefined, cfg: EstadioCfg[]): string | null {
  if (!codigo) return null;
  const cod = codigo.toUpperCase();
  const col = cfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
  if (!col) return null;
  return ((b as Record<string, any>)[col] as string | null) ?? null;
}

// Calendario completo del bloque, solo consulta (modal de Historial).
const FECHAS_BLOQUE: [string, keyof EmpBloque & string][] = [
  ['Siembra',        'fecha_siembra'],
  ['Inducción',      'fecha_induccion'],
  ['Forza',          'fecha_forza'],
  ['Maduración',     'fecha_maduracion'],
  ['Cosecha',        'fecha_cosecha'],
  ['Semillero',      'fecha_semillero'],
  ['Barbecho',       'fecha_barbecho'],
  ['2ª Siembra',     'fecha_segunda'],
  ['2ª Inducción',   'fecha_induccion2'],
  ['2ª Forza',       'fecha_forza2'],
  ['2ª Maduración',  'fecha_maduracion2'],
  ['2ª Cosecha',     'fecha_cosecha2'],
];

// Orden cronológico real del cultivo, separado por CICLO (Primera/PC vs
// Segunda/RC). Comparar fechas de un ciclo contra el otro da falsos positivos:
// un bloque que ya pasó a RC puede conservar un `fecha_induccion` (PC) viejo,
// sin limpiar, sin que eso signifique que su fecha de Semillero (RC) esté mal.
// Por eso cada ciclo se valida SOLO contra sí mismo. fecha_barbecho queda
// afuera a propósito: su ancla real es fecha_siembra (ver ESTADIO_ANCLA_AUTO)
// y su momento en el tiempo no es fijo respecto a las demás.
// Ver docs/CRONOLOGIA_CULTIVO_PINA.md para los rangos de días esperados entre
// cada par (configurables por empresa en app_cronologia_parametros).
interface CicloCronologico {
  label: string;
  campos: (keyof EmpBloque & string)[];
}
const CICLOS_CRONOLOGICOS: CicloCronologico[] = [
  { label: 'Primera cosecha (PC)', campos: ['fecha_siembra', 'fecha_induccion', 'fecha_forza', 'fecha_maduracion', 'fecha_cosecha'] },
  { label: 'Segunda cosecha (RC)', campos: ['fecha_semillero', 'fecha_segunda', 'fecha_induccion2', 'fecha_forza2', 'fecha_maduracion2', 'fecha_cosecha2'] },
];

const ETIQUETA_CRONOLOGICO: Record<string, string> = {
  fecha_siembra: 'Siembra',
  fecha_induccion: 'Inducción',
  fecha_forza: 'Forza',
  fecha_maduracion: 'Maduración',
  fecha_cosecha: 'Cosecha',
  fecha_semillero: 'Semillero',
  fecha_segunda: '2ª Siembra',
  fecha_induccion2: '2ª Inducción',
  fecha_forza2: '2ª Forza',
  fecha_maduracion2: '2ª Maduración',
  fecha_cosecha2: '2ª Cosecha',
};

// Valida que, si se escribe `campo` con `nuevaFecha`, el bloque siga teniendo
// sus fechas-ancla en el orden esperado DENTRO DE SU CICLO (cada fase después
// de la anterior, PC y RC por separado). No es una regla absoluta — el campo
// real es desordenado — así que devuelve un aviso para confirmar, no un
// bloqueo silencioso.
function validarCronologia(
  bloque: EmpBloque, campo: string, nuevaFecha: string,
): { ok: true } | { ok: false; mensaje: string } {
  const ciclo = CICLOS_CRONOLOGICOS.find(c => c.campos.includes(campo as any));
  if (!ciclo) return { ok: true };
  const idx = ciclo.campos.indexOf(campo as any);

  const valores = ciclo.campos.map(col =>
    col === campo ? nuevaFecha : ((bloque as Record<string, any>)[col] as string | null),
  );

  // Fase anterior más cercana con fecha registrada, dentro del mismo ciclo.
  for (let i = idx - 1; i >= 0; i--) {
    const anterior = valores[i];
    if (!anterior) continue;
    if (nuevaFecha < anterior) {
      return {
        ok: false,
        mensaje: `${nuevaFecha} queda ANTES de ${ciclo.campos[i].replace('fecha_', '')} (${anterior}), que debería ser una fase previa.`,
      };
    }
    break;
  }
  // Fase siguiente más cercana con fecha registrada, dentro del mismo ciclo.
  for (let i = idx + 1; i < valores.length; i++) {
    const siguiente = valores[i];
    if (!siguiente) continue;
    if (nuevaFecha > siguiente) {
      return {
        ok: false,
        mensaje: `${nuevaFecha} queda DESPUÉS de ${ciclo.campos[i].replace('fecha_', '')} (${siguiente}), que debería ser una fase posterior.`,
      };
    }
    break;
  }
  return { ok: true };
}

// Parámetro configurable (tabla app_cronologia_parametros): días esperados
// entre dos fechas-ancla, por empresa. Ver CronologiaParametrosPage.tsx.
export interface ParametroCronologia {
  id: number;
  ciclo: 'PC' | 'RC';
  campo_desde: string;
  campo_hasta: string;
  dias_min: number;
  dias_max: number;
  descripcion: string;
  activo: boolean;
}

// Complementa validarCronologia (que solo revisa ORDEN) con el TIEMPO
// transcurrido: si entre dos fechas-ancla configuradas pasaron muchos menos o
// muchos más días de lo típico, algo probablemente está mal aunque el orden
// sea correcto (ej. una inducción digitada 60 días después de la siembra,
// cuando lo normal son 360-450). Los rangos vienen de la tabla, ajustables
// por empresa — sin parámetros activos para ese campo, no avisa nada.
function validarRangoCronologia(
  bloque: EmpBloque, campo: string, nuevaFecha: string, parametros: ParametroCronologia[],
): { ok: true } | { ok: false; mensaje: string } {
  for (const p of parametros) {
    if (!p.activo) continue;
    let fechaDesde: string | null = null;
    let fechaHasta: string | null = null;
    if (campo === p.campo_desde) {
      fechaDesde = nuevaFecha;
      fechaHasta = ((bloque as Record<string, any>)[p.campo_hasta] as string | null) ?? null;
    } else if (campo === p.campo_hasta) {
      fechaHasta = nuevaFecha;
      fechaDesde = ((bloque as Record<string, any>)[p.campo_desde] as string | null) ?? null;
    } else {
      continue;
    }
    if (!fechaDesde || !fechaHasta) continue; // sin la otra fecha no se puede calcular el rango
    const dias = Math.round(
      (new Date(fechaHasta + 'T00:00:00').getTime() - new Date(fechaDesde + 'T00:00:00').getTime()) / 86_400_000,
    );
    if (dias < 0) continue; // el orden ya lo marca validarCronologia
    if (dias < p.dias_min || dias > p.dias_max) {
      const etDesde = ETIQUETA_CRONOLOGICO[p.campo_desde] || p.campo_desde.replace('fecha_', '');
      const etHasta = ETIQUETA_CRONOLOGICO[p.campo_hasta] || p.campo_hasta.replace('fecha_', '');
      return {
        ok: false,
        mensaje: `${dias} días entre ${etDesde} y ${etHasta} — fuera de lo esperado (${p.dias_min}-${p.dias_max} días).`,
      };
    }
  }
  return { ok: true };
}

// Secuencia real de vida del bloque a través de TODO el cultivo (no solo un
// ciclo): sirve para detectar un RETROCESO DE ESTADO real, como el del Lote 48
// (Post Forza RC → Semillero RC → Semillero PC). A diferencia de
// CICLOS_CRONOLOGICOS (que valida fechas dentro de un ciclo), esto compara
// CÓDIGOS DE ESTADO entre ciclos, porque un retroceso de estado casi siempre
// implica también retroceder de ciclo.
// MAD es el único código ambiguo: se usa tanto para la maduración de PC como
// de RC (misma columna fecha_maduracion), así que aparece dos veces y su
// posición real se resuelve con el ciclo vigente al momento del cambio.
const SECUENCIA_ESTADOS: { codigo: string; ciclo: CicloBloque }[] = [
  { codigo: 'PRE',   ciclo: 'PC' },
  { codigo: 'SPC',   ciclo: 'PC' },
  { codigo: 'DPC',   ciclo: 'PC' },
  { codigo: 'FPC',   ciclo: 'PC' },
  { codigo: 'MAD',   ciclo: 'PC' },
  { codigo: 'BAR',   ciclo: 'RC' },
  { codigo: 'SRC',   ciclo: 'RC' },
  { codigo: 'DRC',   ciclo: 'RC' },
  { codigo: 'FRC',   ciclo: 'RC' },
  { codigo: 'MAD',   ciclo: 'RC' },
  { codigo: 'S3RAS', ciclo: 'RC2' },
];

function indiceEstado(codigo: string | null | undefined, ciclo: CicloBloque | null | undefined): number | null {
  const cod = (codigo || '').toUpperCase();
  if (!cod) return null;
  const candidatos = SECUENCIA_ESTADOS
    .map((e, i) => ({ ...e, i }))
    .filter(e => e.codigo === cod);
  if (candidatos.length === 0) return null;
  if (candidatos.length === 1) return candidatos[0].i;
  // Código ambiguo (MAD): usa el ciclo dado; si no coincide con ninguno, la
  // entrada más avanzada (evita bloquear por defecto un caso legítimo).
  return (candidatos.find(e => e.ciclo === ciclo) ?? candidatos[candidatos.length - 1]).i;
}

// ¿El cambio de "bloque" a (nuevoEstadio, nuevoCiclo) retrocede en la
// secuencia de vida del cultivo? No es una fecha mal puesta: es que el estado
// visible del bloque queda "antes" de donde ya estuvo, y eso es exactamente
// lo que pasó en el incidente del Lote 48 sin que nadie lo autorizara.
function esRetrocesoEstado(bloque: EmpBloque, nuevoEstadio: string, nuevoCiclo: CicloBloque): boolean {
  const idxActual = indiceEstado(bloque.estadio_vfp, (bloque.ciclo ?? 'PC') as CicloBloque);
  const idxNuevo = indiceEstado(nuevoEstadio, nuevoCiclo);
  if (idxActual === null || idxNuevo === null) return false;
  return idxNuevo < idxActual;
}

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtF(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function diasDesde(s?: string | null): number | null {
  if (!s) return null;
  return Math.floor((Date.now() - new Date(s + 'T00:00:00').getTime()) / 86_400_000);
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
      style={{
        background: ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
        border: `1px solid ${ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
        color: ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
      }}>
      {msg}
      <button onClick={onClose}><X size={14} /></button>
    </div>,
    document.body
  );
}

// ─── Modal de análisis de cronología ───────────────────────────────────────────
// Muestra la línea de tiempo completa del bloque (ORDEN_CRONOLOGICO) con la
// fecha que se está por escribir en su lugar, marcando en rojo cada quiebre de
// orden. Es la misma comparación que hace validarCronologia, pero visible
// paso a paso en vez de solo la frase final del aviso.
interface PuntoLinea {
  col: string;
  label: string;
  valor: string | null;
  esNuevo: boolean;
  rompeConAnterior: boolean;
}

// campo/nuevaFecha son opcionales: sin ellos, es una consulta de solo lectura
// de la cronología tal como está guardada (botón "Ver cronología"). Devuelve
// una línea POR CICLO — PC y RC nunca se comparan entre sí, para que un
// bloque que ya cambió de ciclo no arrastre falsos positivos del ciclo previo.
function construirLineasPorCiclo(bloque: EmpBloque, campo?: string, nuevaFecha?: string): { label: string; puntos: PuntoLinea[] }[] {
  return CICLOS_CRONOLOGICOS.map(ciclo => {
    const puntos: PuntoLinea[] = ciclo.campos.map(col => ({
      col,
      label: ETIQUETA_CRONOLOGICO[col] || col.replace('fecha_', ''),
      valor: (campo && col === campo && nuevaFecha) ? nuevaFecha : (((bloque as Record<string, any>)[col] as string | null) ?? null),
      esNuevo: !!(campo && col === campo && nuevaFecha),
      rompeConAnterior: false,
    }));
    let ultimaConValor: string | null = null;
    for (const p of puntos) {
      if (!p.valor) continue;
      if (ultimaConValor && p.valor < ultimaConValor) p.rompeConAnterior = true;
      ultimaConValor = p.valor;
    }
    return { label: ciclo.label, puntos };
  });
}

function ModalAnalisisCronologia({
  bloque, campo, nuevaFecha, numBloque, etiquetaResaltada = '(nueva)', onClose,
}: {
  bloque: EmpBloque; campo?: string; nuevaFecha?: string; numBloque?: string | number; etiquetaResaltada?: string; onClose: () => void;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const ciclos = construirLineasPorCiclo(bloque, campo, nuevaFecha);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 flex items-center gap-2 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
          <AlertCircle size={15} style={{ color: 'var(--danger, #dc2626)' }} />
          <span className="text-[13px] font-bold" style={{ color: 'var(--ink)' }}>
            Cronología del bloque{numBloque != null ? ` — BLK ${numBloque}` : ''}
          </span>
          <button onClick={onClose} className="ml-auto"><X size={14} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <p className="text-[11px] -mt-1" style={{ color: 'var(--ink-faint)' }}>
            Radiografía completa: primera y segunda cosecha por separado. En rojo, dónde una fecha queda antes de una fase que debería ser anterior dentro del mismo ciclo.
          </p>
          {ciclos.map(({ label, puntos }) => {
            const conValor = puntos.filter(p => p.valor);
            return (
              <div key={label}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--ink-muted)' }}>
                  {label}
                </p>
                {conValor.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin fechas registradas en este ciclo.</p>
                ) : (
                  <div className="space-y-0.5">
                    {conValor.map((p, i) => (
                      <div key={p.col}>
                        {i > 0 && (
                          <div className="pl-3 text-[13px] leading-none" style={{ color: p.rompeConAnterior ? 'var(--danger, #dc2626)' : 'var(--ink-faint)' }}>
                            {p.rompeConAnterior ? '⚠' : '│'}
                          </div>
                        )}
                        <div className="flex items-center gap-2 py-1 px-2 rounded-md"
                          style={{
                            background: p.esNuevo ? 'var(--emp-accent-bg)' : p.rompeConAnterior ? 'rgba(220,38,38,0.1)' : 'transparent',
                            border: p.rompeConAnterior ? '1px solid var(--danger, #dc2626)' : '1px solid transparent',
                          }}>
                          <span className="text-xs font-semibold flex-1" style={{ color: p.esNuevo ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>
                            {p.label}{p.esNuevo && ` ${etiquetaResaltada}`}
                          </span>
                          <span className="text-xs font-mono" style={{ color: p.rompeConAnterior ? 'var(--danger, #dc2626)' : 'var(--ink-muted)' }}>
                            {fmtF(p.valor)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Modal historial de estados del lote ──────────────────────────────────────

interface FilaHistorial {
  id: number;
  bloque_id: string;
  estadio_anterior: string | null;
  estadio_nuevo: string;
  fecha_cambio: string;
  ancla_columna: string | null;
  ancla_anterior: string | null;
  ancla_nueva: string | null;
  usuario_nombre: string | null;
  origen: string | null;
  nota: string | null;
  created_at: string;
}

function ModalHistorialLote({
  lote, bloques, estadiosCfg, onClose,
}: {
  lote: EmpLote;
  bloques: EmpBloque[];
  estadiosCfg: { codigo: string; descripcion: string }[];
  onClose: () => void;
}) {
  const [filas, setFilas] = useState<FilaHistorial[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true);
      const { data } = await supabase
        .from('app_bloques_estadio_historial')
        .select('id, bloque_id, estadio_anterior, estadio_nuevo, fecha_cambio, ancla_columna, ancla_anterior, ancla_nueva, usuario_nombre, origen, nota, created_at')
        .eq('lote_id', lote.id)
        .order('fecha_cambio', { ascending: false })
        .order('created_at', { ascending: false });
      if (vivo) { setFilas((data ?? []) as FilaHistorial[]); setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [lote.id]);

  // bloque por id, para resolver tanto el número como sus fechas-ancla reales
  const bloquePorId: Record<string, EmpBloque> = {};
  for (const b of bloques) bloquePorId[b.id] = b;

  // num de bloque por id, para no mostrar UUIDs
  const numPorBloque: Record<string, string | number> = {};
  for (const b of bloques) numPorBloque[b.id] = b.num;

  const label = (cod?: string | null) => {
    if (!cod) return '—';
    const c = cod.toUpperCase();
    return estadiosCfg.find(e => e.codigo.toUpperCase() === c)?.descripcion || c;
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="relative flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{ width: 'min(860px, 94vw)', maxHeight: '86vh', background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <div className="flex items-center gap-2">
            <GitBranch size={14} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
              Historial de estados — Lote {lote.numero}
            </span>
            {!cargando && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
                {filas.length}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}>
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {/* Calendario del bloque — solo consulta. Se va llenando con cada
              cambio de estado; no se teclea en ningún lado. */}
          <div className="px-4 pt-3 pb-4" style={{ borderBottom: '1px solid var(--line)' }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ink-muted)' }}>
              Fechas por bloque
            </p>
            <div className="overflow-x-auto">
              <table className="text-[11px]" style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <th className="px-2 py-1 text-left font-bold" style={{ color: 'var(--ink-muted)' }}>BLK</th>
                    <th className="px-2 py-1 text-left font-bold" style={{ color: 'var(--ink-muted)' }}>Estado</th>
                    {FECHAS_BLOQUE.map(([lbl]) => (
                      <th key={lbl} className="px-2 py-1 text-left font-bold whitespace-nowrap"
                        style={{ color: 'var(--ink-muted)' }}>{lbl}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bloques.map(b => (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                      <td className="px-2 py-1 font-mono" style={{ color: 'var(--ink)' }}>{b.num}</td>
                      <td className="px-2 py-1 whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}>
                        {label(b.estadio_vfp)}
                      </td>
                      {FECHAS_BLOQUE.map(([lbl, key]) => (
                        <td key={lbl} className="px-2 py-1 whitespace-nowrap"
                          style={{ color: (b as Record<string, any>)[key] ? 'var(--ink)' : 'var(--ink-faint)' }}>
                          {fmtF((b as Record<string, any>)[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[10px] font-bold uppercase tracking-widest px-4 pt-3 pb-1" style={{ color: 'var(--ink-muted)' }}>
            Cambios de estado
          </p>
          {cargando ? (
            <p className="text-xs text-center py-10" style={{ color: 'var(--ink-faint)' }}>Cargando historial…</p>
          ) : filas.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 px-6 text-center">
              <AlertCircle size={28} style={{ color: 'var(--ink-faint)', opacity: 0.4 }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
                Este lote todavía no tiene cambios de estado registrados.
              </p>
              <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                El historial arranca desde ahora: los cambios anteriores no quedaron guardados.
              </p>
            </div>
          ) : (
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead className="sticky top-0" style={{ background: 'var(--surface-raised)' }}>
                <tr style={{ borderBottom: '1px solid var(--line)' }}>
                  {['Fecha', 'BLK', 'De', 'A', 'Ancla actualizada', 'Usuario', 'Motivo'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: 'var(--ink-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map(f => {
                  const bl = bloquePorId[f.bloque_id];
                  const inicioAnterior = bl ? fechaDeEstadoCodigo(bl, f.estadio_anterior, estadiosCfg) : null;
                  const inicioNuevo = bl ? fechaDeEstadoCodigo(bl, f.estadio_nuevo, estadiosCfg) : null;
                  return (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--ink)' }}>
                      {fmtF(f.fecha_cambio)}
                    </td>
                    <td className="px-3 py-2 font-mono" style={{ color: 'var(--ink-muted)' }}>
                      {numPorBloque[f.bloque_id] ?? '—'}
                    </td>
                    <td className="px-3 py-2" style={{ color: 'var(--ink-muted)' }}>
                      {label(f.estadio_anterior)}
                      {inicioAnterior && (
                        <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>desde {fmtF(inicioAnterior)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-semibold" style={{ color: 'var(--ink)' }}>
                      {label(f.estadio_nuevo)}
                      {inicioNuevo && (
                        <div className="text-[10px] font-normal" style={{ color: 'var(--ink-faint)' }}>desde {fmtF(inicioNuevo)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                      {f.ancla_columna
                        ? <>{f.ancla_columna.replace('fecha_', '')}: {fmtF(f.ancla_anterior)} → <strong style={{ color: 'var(--ink)' }}>{fmtF(f.ancla_nueva)}</strong></>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                      {f.usuario_nombre ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-[11px] max-w-[220px]" style={{ color: 'var(--ink-muted)' }}>
                      {f.origen === 'correccion'
                        ? <span title={f.nota ?? undefined}><span style={{ color: 'var(--emp-accent-txt)' }}>Corrección: </span>{f.nota ?? '—'}</span>
                        : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Modal resumen agrupado del lote ───────────────────────────────────────────
// Agrupa los bloques del lote por (estado, fecha-ancla, grupo forza) y muestra
// rangos de BLK en vez de una fila por bloque — la misma "radiografía" que se
// arma a mano al verificar una corrección masiva (ej. Lote 48: BLK 1-4 / 5-8 /
// 9-15, cada grupo con su propia fecha real). Puramente de lectura.
interface GrupoResumenLote {
  rango: string;
  nBloques: number;
  estadioCodigo: string;
  estadioLabel: string;
  fecha: string | null;
  grupoForza: string | null;
  plantas: number;
  areaSiem: number;
}

// Convierte una lista de números de bloque en rangos legibles: [1,2,3,4,7] -> "1-4, 7"
function formatearRangoBlk(nums: (number | string)[]): string {
  const ordenados = [...new Set(nums.map(n => Number(n)))].filter(n => !Number.isNaN(n)).sort((a, b) => a - b);
  const rangos: string[] = [];
  let inicio = ordenados[0];
  let previo = ordenados[0];
  for (let i = 1; i <= ordenados.length; i++) {
    const actual = ordenados[i];
    if (actual === previo + 1) { previo = actual; continue; }
    rangos.push(inicio === previo ? `${inicio}` : `${inicio}-${previo}`);
    inicio = actual;
    previo = actual;
  }
  return rangos.join(', ');
}

function agruparBloquesPorEstado(bloques: EmpBloque[], estadiosCfg: EstadioCfg[]): GrupoResumenLote[] {
  const grupos = new Map<string, { nums: (number | string)[]; b: EmpBloque[] }>();
  for (const b of bloques) {
    const cod = (b.estadio_vfp ?? '').toUpperCase();
    const fecha = fechaInicioFase(b, estadiosCfg);
    const gf = b.grupo_forza ?? '';
    const key = `${cod}|${fecha ?? ''}|${gf}`;
    if (!grupos.has(key)) grupos.set(key, { nums: [], b: [] });
    const g = grupos.get(key)!;
    g.nums.push(b.num);
    g.b.push(b);
  }
  const out: GrupoResumenLote[] = [];
  for (const { nums, b } of grupos.values()) {
    const cod = (b[0].estadio_vfp ?? '').toUpperCase();
    out.push({
      rango: formatearRangoBlk(nums),
      nBloques: b.length,
      estadioCodigo: cod,
      estadioLabel: estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.descripcion || cod || '—',
      fecha: fechaInicioFase(b[0], estadiosCfg),
      grupoForza: b[0].grupo_forza || null,
      plantas: b.reduce((s, x) => s + (x.plant_count || 0), 0),
      areaSiem: b.reduce((s, x) => s + (x.area_ha || 0), 0),
    });
  }
  // Orden natural: por el primer número de BLK de cada grupo.
  out.sort((a, b) => (Number(a.rango.split(/[-,]/)[0]) || 0) - (Number(b.rango.split(/[-,]/)[0]) || 0));
  return out;
}

function ModalResumenLote({
  lote, bloques, estadiosCfg, onClose,
}: {
  lote: EmpLote;
  bloques: EmpBloque[];
  estadiosCfg: EstadioCfg[];
  onClose: () => void;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const grupos = agruparBloquesPorEstado(bloques, estadiosCfg);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="relative flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{ width: 'min(720px, 94vw)', maxHeight: '86vh', background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <div className="flex items-center gap-2">
            <Layers size={14} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
              Resumen — Lote {lote.numero}
            </span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
              {grupos.length} grupo{grupos.length === 1 ? '' : 's'}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}>
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-raised)' }}>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Grupo</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Estado</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Fecha</th>
                <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Grupo Forza</th>
                <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Plantas</th>
                <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Área (ha)</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g, i) => {
                const color = VFP_COLOR[g.estadioCodigo] || VFP_COLOR_FALLBACK;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <td className="px-4 py-2 font-mono font-semibold" style={{ color: 'var(--ink)' }}>
                      BLK {g.rango} <span style={{ color: 'var(--ink-faint)' }}>({g.nBloques})</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}>
                        {g.estadioLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono" style={{ color: 'var(--ink)' }}>{fmtF(g.fecha)}</td>
                    <td className="px-4 py-2" style={{ color: 'var(--ink-muted)' }}>{g.grupoForza || '—'}</td>
                    <td className="px-4 py-2 text-right font-mono" style={{ color: 'var(--ink)' }}>{g.plantas.toLocaleString('es-CR')}</td>
                    <td className="px-4 py-2 text-right font-mono" style={{ color: 'var(--ink)' }}>{g.areaSiem.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Modal imagen lote ────────────────────────────────────────────────────────

function ModalImagenLote({ lote, onClose }: { lote: EmpLote; onClose: () => void }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="relative flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{ maxWidth: '90vw', maxHeight: '90vh', background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <div className="flex items-center gap-2">
            <ImageIcon size={14} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>
              {lote.nombre}
            </span>
            <span className="text-xs font-mono" style={{ color: 'var(--ink-muted)' }}>
              Lote {lote.numero}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded transition-colors"
            style={{ color: 'var(--ink-muted)' }}>
            <X size={16} />
          </button>
        </div>
        {/* Imagen */}
        <div className="flex-1 overflow-hidden flex items-center justify-center"
          style={{ minHeight: '300px', background: '#000' }}>
          {lote.raster_png_url ? (
            <img
              src={lote.raster_png_url}
              alt={`Lote ${lote.numero}`}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: '80vh' }}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 opacity-40 py-16 px-10">
              <ImageIcon size={48} style={{ color: 'var(--ink-faint)' }} />
              <p className="text-sm text-center" style={{ color: 'var(--ink-faint)' }}>
                No hay imagen disponible para este lote.<br />
                <span className="text-xs">Cargá el raster PNG desde Dron → Polígono.</span>
              </p>
            </div>
          )}
        </div>
        {/* Footer con metadatos */}
        {lote.raster_uploaded_at && (
          <div className="px-4 py-2 shrink-0 text-[11px]"
            style={{ borderTop: '1px solid var(--line-dim)', background: 'var(--surface-raised)', color: 'var(--ink-muted)' }}>
            Imagen cargada: {new Date(lote.raster_uploaded_at).toLocaleDateString('es-CR')}
            {lote.raster_filename && <span className="ml-3 font-mono opacity-60">{lote.raster_filename}</span>}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Panel de Cambios ─────────────────────────────────────────────────────────

// El panel permite corregir el ancla de la fase actual cuando se selecciona un
// solo bloque. Esto es necesario cuando el cambio de estadio ya ocurrió, pero
// la fecha (por ejemplo fecha_induccion2 en FRC) quedó ausente o incorrecta.
interface CambioForm {
  estadio_vfp: string;          // código de estadio (DPC/FPC/DRC/FRC/SRC/SPC/BAR/PRE) — lo que lee el generador
  ciclo: CicloBloque;
  grupo_forza: string;
  // Fecha real de inicio de la fase: alimenta el historial y el ancla del estadio
  fecha_cambio: string;
  fecha_ancla: string;
  // Motivo obligatorio cuando se corrige fecha_ancla sin cambiar de estado: la
  // corrección pisa un dato agronómico ya usado por boletas/reportes, así que
  // debe quedar trazada en el historial (quién, cuándo, por qué).
  motivo_correccion: string;
}

function PanelCambios({
  seleccionados, bloques, estadiosCfg, onAplicado, usuarioNombre, permVoB,
}: {
  seleccionados: Set<string>;
  bloques: EmpBloque[];
  estadiosCfg: EstadioCfg[];
  onAplicado: () => void;
  usuarioNombre?: string;
  // V°B° Ingeniero: la corrección de fecha-ancla mueve el calendario de boletas
  // y muestreos del bloque, así que solo un ingeniero (perm_vob) puede aplicarla.
  permVoB: boolean;
}) {
  const n = seleccionados.size;
  const empresaId = useEmpresaId();

  // Panel colapsable: ocupa espacio de la tabla (vista analítica) cuando no
  // hay nada que editar. Se abre solo al marcar bloques y se cierra solo al
  // quedar en cero; el botón de la franja permite abrirlo/cerrarlo a mano en
  // cualquier momento sin esperar a que cambie la selección.
  const [panelAbierto, setPanelAbierto] = useState(false);
  useEffect(() => {
    setPanelAbierto(n > 0);
  }, [n]);

  const [form, setForm] = useState<CambioForm>({
    estadio_vfp: '', ciclo: 'PC', grupo_forza: '', fecha_cambio: hoyISO(), fecha_ancla: '', motivo_correccion: '',
  });

  // Ingenieros con V°B° de la empresa, solo para mostrar a quién se le va a
  // notificar. Antes se elegía UNO por un selector (o quedaba al azar entre
  // los que tienen V°B° si no se tocaba) y a veces el mensaje llegaba al
  // ingeniero equivocado. Ahora el backend notifica a TODOS los de esta
  // lista con el mismo link — el primero que lo resuelve gana.
  interface IngenieroOpt {
    user_id: string;
    email: string | null;
    nombre: string;
    phone: string | null;
    rol_codigo: string | null;
    rol_nombre: string | null;
  }
  const [ingenieros, setIngenieros] = useState<IngenieroOpt[]>([]);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl('/aplicaciones/ingenieros'), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (!vivo) return;
        if (resp.ok && Array.isArray(j.ingenieros)) {
          setIngenieros(j.ingenieros);
        }
      } catch { /* sin ingenieros configurados, se deja al fallback del servidor */ }
    })();
    return () => { vivo = false; };
  }, [empresaId]);

  // Rangos configurables de días entre fechas-ancla (CronologiaParametrosPage).
  // Complementa validarCronologia (orden) con el tiempo transcurrido.
  const [cronologiaParametros, setCronologiaParametros] = useState<ParametroCronologia[]>([]);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data } = await supabase
        .from('app_cronologia_parametros')
        .select('id, ciclo, campo_desde, campo_hasta, dias_min, dias_max, descripcion, activo')
        .eq('empresa_id', empresaId)
        .eq('activo', true);
      if (vivo) setCronologiaParametros((data ?? []) as ParametroCronologia[]);
    })();
    return () => { vivo = false; };
  }, [empresaId]);

  const [aplicando, setAplicando] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [solicitando, setSolicitando] = useState(false);
  // Confirmación explícita cuando la fecha rompe el orden cronológico del
  // cultivo (ver ORDEN_CRONOLOGICO). Se resetea cada vez que cambia la fecha
  // para que una confirmación vieja no tape un aviso nuevo.
  const [confirmarCronologia, setConfirmarCronologia] = useState(false);
  // Aviso de cronología cuya línea de tiempo completa se está mostrando.
  const [analisisAbierto, setAnalisisAbierto] = useState<{ bloque: EmpBloque; campo: string; fecha: string } | null>(null);
  // Última etapa con boleta REALMENTE aplicada en campo, por número de bloque.
  // Es la única fuente que no depende de lo que alguien tecleó en Estadios —
  // por eso sirve para detectar un retroceso de estado como el del Lote 48.
  const [ultimaEtapaBoletas, setUltimaEtapaBoletas] = useState<Record<string, { etapa_nombre: string; fecha_desde: string; numero: string }>>({});
  const [confirmarBoletas, setConfirmarBoletas] = useState(false);

  useEffect(() => {
    const loteId = bloques[0]?.lote_id;
    if (!loteId) { setUltimaEtapaBoletas({}); return; }
    let vivo = true;
    (async () => {
      const { data } = await supabase
        .from('app_boletas')
        .select('bloques, etapa_nombre, fecha_desde, numero')
        .eq('lote_id', loteId)
        .eq('estado', 'aplicada')
        .not('etapa_nombre', 'is', null)
        .order('fecha_desde', { ascending: false })
        .limit(300);
      if (!vivo) return;
      const porNum: Record<string, { etapa_nombre: string; fecha_desde: string; numero: string }> = {};
      for (const fila of (data ?? [])) {
        const nums = String(fila.bloques ?? '').split(',').map(s => s.trim()).filter(Boolean);
        for (const num of nums) {
          if (!porNum[num]) porNum[num] = fila as any; // ya viene ordenado desc: la primera vez que aparece es la más reciente
        }
      }
      setUltimaEtapaBoletas(porNum);
    })();
    return () => { vivo = false; };
  }, [bloques]);

  // Cambios de estado recientes (últimos 15 min) por bloque. Varios cambios
  // al mismo bloque en minutos es el patrón de alguien probando la pantalla o
  // equivocándose de clic — no de un evento real de campo (así se detectó el
  // FRC→SRC→SPC→SRC del incidente del Lote 48).
  const [historialReciente, setHistorialReciente] = useState<Record<string, { estadio_anterior: string | null; estadio_nuevo: string; created_at: string }[]>>({});
  const [confirmarCadena, setConfirmarCadena] = useState(false);

  // Medida 5 (incidente Lote 48): un retroceso de estado (ej. Post Forza RC →
  // Semillero RC) mueve el estado visible del bloque hacia atrás en el
  // cultivo, igual de sensible que corregir la fecha-ancla — requiere V°B°.
  const [confirmarRetroceso, setConfirmarRetroceso] = useState(false);
  const [solicitandoRetroceso, setSolicitandoRetroceso] = useState(false);

  useEffect(() => {
    const loteId = bloques[0]?.lote_id;
    if (!loteId) { setHistorialReciente({}); return; }
    let vivo = true;
    (async () => {
      const desde = new Date(Date.now() - 15 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('app_bloques_estadio_historial')
        .select('bloque_id, estadio_anterior, estadio_nuevo, created_at')
        .eq('lote_id', loteId)
        .gte('created_at', desde)
        .order('created_at', { ascending: false });
      if (!vivo) return;
      const porBloque: Record<string, { estadio_anterior: string | null; estadio_nuevo: string; created_at: string }[]> = {};
      for (const fila of (data ?? [])) {
        if (!porBloque[fila.bloque_id]) porBloque[fila.bloque_id] = [];
        porBloque[fila.bloque_id].push(fila);
      }
      setHistorialReciente(porBloque);
    })();
    return () => { vivo = false; };
  }, [bloques]);

  // Cuando cambia la selección a exactamente 1 bloque, pre-llenar el form.
  // Para cualquier otro tamaño de selección (0 o varios) se resetea a valores
  // neutros — sin esto, elegir un bloque y después sumar otro con distinto
  // estadio dejaba pegado el "estadio_vfp" del primero, y eso disparaba
  // "Falta el motivo del cambio de estado" sin que el usuario tocara nada.
  useEffect(() => {
    if (seleccionados.size === 1) {
      const b = bloques.find(x => seleccionados.has(x.id));
      if (b) {
        const codigo = (b.estadio_vfp ?? '').toUpperCase();
        const colAncla = estadiosCfg.find(e => e.codigo.toUpperCase() === codigo)?.varfecha || ESTADIO_ANCLA_AUTO[codigo];
        setForm({
          estadio_vfp:  codigo,
          ciclo:        (b.ciclo ?? 'PC') as CicloBloque,
          grupo_forza:  b.grupo_forza ?? '',
          fecha_cambio: hoyISO(),
          fecha_ancla:  colAncla ? String((b as Record<string, any>)[colAncla] ?? '').slice(0, 10) : '',
          motivo_correccion: '',
        });
      }
    } else {
      setForm(f => ({ ...f, estadio_vfp: '', fecha_ancla: '', motivo_correccion: '' }));
    }
    setConfirmarCronologia(false);
    setConfirmarBoletas(false);
    setConfirmarCadena(false);
    setConfirmarRetroceso(false);
  }, [seleccionados, bloques, estadiosCfg]);

  function set<K extends keyof CambioForm>(k: K, v: CambioForm[K]) {
    if (k === 'fecha_ancla' || k === 'fecha_cambio') setConfirmarCronologia(false);
    if (k === 'estadio_vfp' || k === 'fecha_cambio') { setConfirmarBoletas(false); setConfirmarCadena(false); }
    if (k === 'estadio_vfp' || k === 'ciclo') setConfirmarRetroceso(false);
    setForm(f => ({ ...f, [k]: v }));
  }

  async function aplicar() {
    if (n === 0) return;
    const ids = [...seleccionados];
    const nuevoEstadio = (form.estadio_vfp || '').toUpperCase();
    const seleccion = bloques.filter(b => seleccionados.has(b.id));

    // Bloques que se quedan en su estadio actual (no hay transición para ellos).
    // A esos se les puede corregir la fecha-ancla sin mover el estado. Si no se
    // eligió un nuevo estado, ninguno de los seleccionados cambia, así que todos
    // entran en este grupo.
    const bloquesSinCambio = nuevoEstadio
      ? seleccion.filter(b => (b.estadio_vfp ?? '').toUpperCase() === nuevoEstadio)
      : seleccion;
    // "Fecha ancla actual" se pre-llena con el valor que ya tiene el bloque
    // (para mostrarlo) — solo cuenta como corrección si el usuario lo cambió
    // de verdad. Si no, no hay nada que motivar ni escribir.
    const hayCorreccionAncla = !!form.fecha_ancla && bloquesSinCambio.some(b => {
      const cod = (b.estadio_vfp ?? '').toUpperCase();
      const col = estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
      if (!col) return false;
      const actual = String((b as Record<string, any>)[col] ?? '').slice(0, 10);
      return actual !== form.fecha_ancla;
    });

    // Bloques que realmente cambian de estadio: solo a ellos se les mueve el
    // ancla y solo ellos generan fila de historial. Se calcula acá (no más
    // abajo) porque las validaciones de motivo y fecha futura lo necesitan.
    const cambianEstadio = nuevoEstadio
      ? seleccion.filter(b => (b.estadio_vfp ?? '').toUpperCase() !== nuevoEstadio)
      : [];

    // El cambio de estado pisa la fecha-ancla igual que la corrección directa
    // y mueve el calendario de boletas/muestreos: exige motivo por la misma
    // razón (trazabilidad de quién y por qué).
    if (cambianEstadio.length > 0 && !form.motivo_correccion.trim()) {
      setToast({ msg: 'Indicá el motivo del cambio de estado.', ok: false });
      return;
    }
    // Una "Fecha del cambio" futura no puede corresponder a un evento de campo
    // que ya ocurrió — el incidente del Lote 48 empezó justo con una fecha de
    // cambio programada para el futuro.
    if (cambianEstadio.length > 0 && form.fecha_cambio > hoyISO()) {
      setToast({ msg: 'La fecha del cambio no puede ser futura.', ok: false });
      return;
    }

    // La corrección de fecha-ancla mueve el calendario de boletas y muestreos
    // del bloque: solo un ingeniero con V°B° (perm_vob) puede autorizarla. Se
    // revalida acá además de ocultarla en la UI, por si el permiso cambió.
    if (hayCorreccionAncla && !permVoB) {
      setToast({ msg: 'Corregir la fecha-ancla requiere autorización de ingeniería (V°B°).', ok: false });
      return;
    }

    // La corrección de fecha-ancla pisa un dato que ya usan boletas y reportes,
    // así que exige un motivo: sin él no queda claro por qué cambió y no hay
    // forma de auditarlo después.
    if (hayCorreccionAncla && !form.motivo_correccion.trim()) {
      setToast({ msg: 'Indicá el motivo de la corrección de fecha.', ok: false });
      return;
    }

    // Medida 5 (incidente Lote 48): un retroceso de estado real (no una fecha
    // mal puesta) requiere el mismo V°B° que la corrección de ancla. Se
    // revalida acá por si el permiso cambió o la UI quedó desincronizada.
    const bloquesConRetroceso = cambianEstadio.filter(b => esRetrocesoEstado(b, nuevoEstadio, form.ciclo));
    if (bloquesConRetroceso.length > 0 && !permVoB) {
      setToast({ msg: 'Este cambio retrocede el estado del bloque: requiere autorización de ingeniería (V°B°). Usá "Solicitar autorización" más abajo.', ok: false });
      return;
    }
    if (bloquesConRetroceso.length > 0 && !confirmarRetroceso) {
      setToast({ msg: 'Confirmá el aviso de retroceso de estado antes de aplicar.', ok: false });
      return;
    }

    // Defensa adicional: revalida el aviso de cronología por si el estado de
    // la UI quedó desincronizado (mismo criterio que el botón deshabilitado).
    if (cronologiaSinConfirmar) {
      setToast({ msg: 'Confirmá el aviso de cronología antes de aplicar.', ok: false });
      return;
    }
    if (boletasSinConfirmar) {
      setToast({ msg: 'Confirmá el aviso de boletas ya aplicadas antes de continuar.', ok: false });
      return;
    }
    if (cadenaSinConfirmar) {
      setToast({ msg: 'Confirmá el aviso de cambios en cadena antes de continuar.', ok: false });
      return;
    }

    setAplicando(true);

    // Ninguna fecha viaja aquí: el panel ya no las edita. La única fecha que se
    // escribe es el ancla del nuevo estadio, más abajo y solo para los bloques
    // que efectivamente cambian de estado.
    const payload: Record<string, unknown> = { ciclo: form.ciclo };
    // estadio_vfp es lo que lee el generador de boletas. Solo lo escribimos si el
    // usuario eligió uno (en edición múltiple, dejar en blanco = no tocar el estadio).
    if (form.estadio_vfp) payload.estadio_vfp = form.estadio_vfp;

    // El Grupo de Forza solo se escribe si el estadio resultante lo admite; en
    // cualquier otro caso se omite del payload para no pisar el dato existente.
    const estadioResultanteAdmiteGrupo = nuevoEstadio
      ? tieneGrupoForza(nuevoEstadio)
      : seleccion.every(b => tieneGrupoForza(b.estadio_vfp));
    if (estadioResultanteAdmiteGrupo) payload.grupo_forza = form.grupo_forza || null;

    // La corrección se agrupa por columna de ancla porque, en una selección
    // múltiple, bloques con distinto estadio actual corrigen columnas distintas.
    // Se guarda el valor anterior de cada bloque para dejarlo trazado en el
    // historial (quién, cuándo, por qué se corrigió).
    const filasCorreccion: Record<string, unknown>[] = [];
    if (hayCorreccionAncla) {
      const porColumna = new Map<string, EmpBloque[]>();
      for (const b of bloquesSinCambio) {
        const cod = (b.estadio_vfp ?? '').toUpperCase();
        const col = estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
        if (!col) continue;
        if (!porColumna.has(col)) porColumna.set(col, []);
        porColumna.get(col)!.push(b);
      }
      const columnas = [...porColumna.entries()];
      // Si toda la selección corrige la misma columna, se suma al update general;
      // si no, se aplican updates aparte por grupo.
      if (columnas.length === 1 && columnas[0][1].length === ids.length) {
        payload[columnas[0][0]] = form.fecha_ancla;
      } else {
        for (const [col, bloquesCol] of columnas) {
          const { error: eCorr } = await supabase.from('emp_bloques').update({ [col]: form.fecha_ancla }).in('id', bloquesCol.map(b => b.id));
          if (eCorr) {
            setAplicando(false);
            setToast({ msg: `Error corrigiendo fecha ancla: ${eCorr.message}`, ok: false });
            return;
          }
        }
      }
      for (const [col, bloquesCol] of columnas) {
        for (const b of bloquesCol) {
          filasCorreccion.push({
            empresa_id:       empresaId,
            bloque_id:        b.id,
            lote_id:          b.lote_id ?? null,
            estadio_anterior: (b.estadio_vfp ?? '').toUpperCase() || null,
            estadio_nuevo:    (b.estadio_vfp ?? '').toUpperCase() || null,
            fecha_cambio:     form.fecha_ancla,
            ancla_columna:    col,
            ancla_anterior:   (b as Record<string, any>)[col] ?? null,
            ancla_nueva:      form.fecha_ancla,
            usuario_nombre:   usuarioNombre || null,
            origen:           'correccion',
            nota:             form.motivo_correccion.trim(),
          });
        }
      }
    }

    const { error } = await supabase.from('emp_bloques').update(payload).in('id', ids);
    if (error) {
      setAplicando(false);
      setToast({ msg: `Error: ${error.message}`, ok: false });
      return;
    }

    // La fecha del cambio es la fecha-ancla del nuevo estadio: se escribe después
    // del update general para que mande sobre lo que hubiera en el formulario.
    const anclaCol = anclaDelEstadio(nuevoEstadio);
    const bloquesConAncla = cambianEstadio.filter(b => anclaDelCambio(nuevoEstadio, b.estadio_vfp));
    if (anclaCol && form.fecha_cambio && bloquesConAncla.length > 0) {
      const { error: eAncla } = await supabase
        .from('emp_bloques')
        .update({ [anclaCol]: form.fecha_cambio })
        .in('id', bloquesConAncla.map(b => b.id));
      if (eAncla) {
        setAplicando(false);
        setToast({ msg: `Estado guardado, pero falló el ancla: ${eAncla.message}`, ok: false });
        return;
      }
    }

    if (cambianEstadio.length > 0) {
      filasCorreccion.push(...cambianEstadio.map(b => {
        const anclaBloque = anclaDelCambio(nuevoEstadio, b.estadio_vfp);
        return {
        empresa_id:       empresaId,
        bloque_id:        b.id,
        lote_id:          b.lote_id ?? null,
        estadio_anterior: (b.estadio_vfp ?? '').toUpperCase() || null,
        estadio_nuevo:    nuevoEstadio,
        fecha_cambio:     form.fecha_cambio || hoyISO(),
        ancla_columna:    anclaBloque ?? null,
        ancla_anterior:   anclaBloque ? ((b as Record<string, any>)[anclaBloque] ?? null) : null,
        ancla_nueva:      anclaBloque ? (form.fecha_cambio || null) : null,
        usuario_nombre:   usuarioNombre || null,
        origen:           'ui',
        nota:             form.motivo_correccion.trim(),
        };
      }));
    }

    if (filasCorreccion.length > 0) {
      // El historial es bitácora: si falla, no se revierte el cambio agronómico.
      const { error: eHist } = await supabase.from('app_bloques_estadio_historial').insert(filasCorreccion);
      if (eHist) {
        setAplicando(false);
        setToast({ msg: `Cambio aplicado, pero no se registró el historial: ${eHist.message}`, ok: false });
        onAplicado();
        return;
      }
    }

    setAplicando(false);
    const sufijoAncla = anclaCol && bloquesConAncla.length > 0
      ? ` · ancla ${anclaCol.replace('fecha_', '')} = ${form.fecha_cambio}`
      : hayCorreccionAncla ? ` · fecha ancla corregida (${bloquesSinCambio.length})` : '';
    setToast({ msg: `${n} bloque${n > 1 ? 's' : ''} actualizado${n > 1 ? 's' : ''}${sufijoAncla}`, ok: true });
    setForm(f => ({ ...f, motivo_correccion: '' }));
    onAplicado();
  }

  const primerBloque = seleccionados.size === 1
    ? bloques.find(x => seleccionados.has(x.id))
    : null;

  const codVfpActual = primerBloque ? (primerBloque.estadio_vfp ?? '').toUpperCase() : '';
  const estadoActualLabel = codVfpActual
    ? (estadiosCfg.find(e => e.codigo.toUpperCase() === codVfpActual)?.descripcion || codVfpActual)
    : null;

  // ¿Algún bloque seleccionado cambia de estadio con lo elegido en el form?
  const estadioElegido = (form.estadio_vfp || '').toUpperCase();
  const hayCambioEstado = !!estadioElegido && bloques.some(
    b => seleccionados.has(b.id) && (b.estadio_vfp ?? '').toUpperCase() !== estadioElegido,
  );
  const anclaDelNuevoEstado: string | undefined = anclaDelEstadio(estadioElegido);

  // Bloques seleccionados que no cambian de estadio (todos, si no se eligió uno
  // nuevo): a esos se les puede corregir la fecha-ancla, sea 1 bloque o varios.
  const bloquesSinCambioUI = estadioElegido
    ? bloques.filter(b => seleccionados.has(b.id) && (b.estadio_vfp ?? '').toUpperCase() === estadioElegido)
    : bloques.filter(b => seleccionados.has(b.id));
  const anclaColumnasSinCambio = new Set(
    bloquesSinCambioUI
      .map(b => {
        const cod = (b.estadio_vfp ?? '').toUpperCase();
        return estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
      })
      .filter((c): c is string => !!c),
  );
  const anclaDelEstadoActual = anclaColumnasSinCambio.size === 1 ? [...anclaColumnasSinCambio][0] : undefined;

  // Corregir la fecha-ancla mueve el calendario de boletas/muestreos del
  // bloque, así que solo se ofrece a usuarios con V°B° de ingeniería.
  const puedeCorregirAncla = !hayCambioEstado && bloquesSinCambioUI.length > 0 && anclaColumnasSinCambio.size > 0;

  // El campo "Fecha ancla actual" se pre-llena solo con el valor que YA tiene
  // el bloque (para mostrarlo), no es una corrección todavía. Sin esta
  // comparación, entrar a Estadios y marcar un solo bloque ya disparaba "Falta
  // el motivo" aunque nadie hubiera tocado nada.
  const valorAnclaOriginal = (bloquesSinCambioUI.length === 1 && anclaDelEstadoActual)
    ? String((bloquesSinCambioUI[0] as Record<string, any>)[anclaDelEstadoActual] ?? '').slice(0, 10)
    : null;
  const huboCorreccionAncla = !!form.fecha_ancla && (valorAnclaOriginal === null || form.fecha_ancla !== valorAnclaOriginal);

  // Corregir la fecha-ancla sin motivo dejaría el cambio sin trazar, así que
  // bloquea el botón Aplicar mientras falte. Un cambio de estado real (no solo
  // corrección de fecha) exige lo mismo, por el mismo motivo.
  const requiereMotivoCorreccion = (permVoB && puedeCorregirAncla && huboCorreccionAncla) || hayCambioEstado;
  const motivoFaltante = requiereMotivoCorreccion && !form.motivo_correccion.trim();
  // Una fecha de cambio futura no puede corresponder a un evento de campo ya
  // ocurrido — bloquea el botón igual que el motivo faltante.
  const fechaCambioFutura = hayCambioEstado && form.fecha_cambio > hoyISO();

  // Para poder ARMAR la solicitud (sin V°B°) los bloques sin cambio deben
  // compartir el mismo estadio actual, no solo la misma columna de ancla
  // (BAR/PRE/DPC comparten fecha_siembra pero son estadios distintos).
  const estadiosSinCambioUnicos = new Set(bloquesSinCambioUI.map(b => (b.estadio_vfp ?? '').toUpperCase()));
  const estadioCodigoSolicitud = estadiosSinCambioUnicos.size === 1 ? [...estadiosSinCambioUnicos][0] : undefined;

  // Bloques que sí cambian de estadio con lo elegido (para el chequeo de
  // cronología de la "Fecha del cambio").
  const cambianEstadioUI = estadioElegido
    ? bloques.filter(b => seleccionados.has(b.id) && (b.estadio_vfp ?? '').toUpperCase() !== estadioElegido)
    : [];

  // Avisos de cronología: la fecha que se va a escribir queda antes/después de
  // otra fecha-ancla del mismo bloque que debería ir en el orden contrario.
  // No bloquea solo: exige que el usuario tilde "aplicar de todas formas".
  // Guardan el bloque+campo para poder abrir el modal de análisis por aviso.
  const avisosCronologiaCorreccion: { mensaje: string; bloque: EmpBloque; campo: string }[] = [];
  if (huboCorreccionAncla) {
    const vistos = new Set<string>();
    for (const b of bloquesSinCambioUI) {
      const cod = (b.estadio_vfp ?? '').toUpperCase();
      const col = estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
      if (!col) continue;
      const r = validarCronologia(b, col, form.fecha_ancla);
      if (!r.ok && !vistos.has(r.mensaje)) {
        vistos.add(r.mensaje);
        avisosCronologiaCorreccion.push({ mensaje: r.mensaje, bloque: b, campo: col });
      }
      const rRango = validarRangoCronologia(b, col, form.fecha_ancla, cronologiaParametros);
      if (!rRango.ok && !vistos.has(rRango.mensaje)) {
        vistos.add(rRango.mensaje);
        avisosCronologiaCorreccion.push({ mensaje: rRango.mensaje, bloque: b, campo: col });
      }
    }
  }
  const avisosCronologiaCambio: { mensaje: string; bloque: EmpBloque; campo: string }[] = [];
  // El orden de la cronología pone Semillero antes de la 2ª siembra (SPC), pero
  // SRC viene después de la 2ª cosecha: validarlo daría una alerta falsa.
  if (hayCambioEstado && anclaDelNuevoEstado && !ESTADIOS_SEMILLERO.has(estadioElegido) && form.fecha_cambio) {
    const vistos = new Set<string>();
    for (const b of cambianEstadioUI) {
      const r = validarCronologia(b, anclaDelNuevoEstado, form.fecha_cambio);
      if (!r.ok && !vistos.has(r.mensaje)) {
        vistos.add(r.mensaje);
        avisosCronologiaCambio.push({ mensaje: r.mensaje, bloque: b, campo: anclaDelNuevoEstado });
      }
      const rRango = validarRangoCronologia(b, anclaDelNuevoEstado, form.fecha_cambio, cronologiaParametros);
      if (!rRango.ok && !vistos.has(rRango.mensaje)) {
        vistos.add(rRango.mensaje);
        avisosCronologiaCambio.push({ mensaje: rRango.mensaje, bloque: b, campo: anclaDelNuevoEstado });
      }
    }
  }
  const avisosCronologia = [...avisosCronologiaCorreccion, ...avisosCronologiaCambio];
  const cronologiaSinConfirmar = avisosCronologia.length > 0 && !confirmarCronologia;

  // Cruce contra la última boleta REAL aplicada: si ya se le aplicó al bloque
  // una etapa distinta a la que se está por guardar, es la señal más directa
  // de que el estado nuevo no corresponde a lo que pasó en el campo.
  // Se normaliza cortando en " - " porque la descripción del catálogo trae
  // sufijos (ej. "Maduración - Solo Boletas Manuales") que la boleta no tiene.
  const normalizarEtapa = (s: string) => s.split(' - ')[0].trim().toLowerCase();
  const estadioElegidoLabel = estadioElegido
    ? (estadiosCfg.find(e => e.codigo.toUpperCase() === estadioElegido)?.descripcion || estadioElegido)
    : '';
  const avisosBoletas: { mensaje: string; bloque: EmpBloque }[] = [];
  if (hayCambioEstado) {
    const vistos = new Set<string>();
    for (const b of cambianEstadioUI) {
      const ultima = ultimaEtapaBoletas[String(b.num)];
      if (!ultima) continue;
      const etapaBoleta = String(ultima.etapa_nombre || '').trim();
      if (!etapaBoleta || normalizarEtapa(etapaBoleta) === normalizarEtapa(estadioElegidoLabel)) continue;
      const msg = `La boleta ${ultima.numero} (${fmtF(ultima.fecha_desde)}) ya registró este bloque en "${etapaBoleta}", distinto de "${estadioElegidoLabel}".`;
      if (!vistos.has(msg)) { vistos.add(msg); avisosBoletas.push({ mensaje: msg, bloque: b }); }
    }
  }
  const boletasSinConfirmar = avisosBoletas.length > 0 && !confirmarBoletas;

  // Aviso por cambios en cadena: el mismo bloque ya cambió de estado hace muy
  // poco. Un evento de campo real casi nunca se retoca dos veces en minutos.
  const avisosCadena: { mensaje: string; bloque: EmpBloque }[] = [];
  if (hayCambioEstado) {
    for (const b of cambianEstadioUI) {
      const previos = historialReciente[b.id];
      if (!previos || previos.length === 0) continue;
      const ultimo = previos[0];
      const minutos = Math.max(1, Math.round((Date.now() - new Date(ultimo.created_at).getTime()) / 60000));
      const plural = previos.length > 1 ? `${previos.length} cambios` : '1 cambio';
      const msg = `Este bloque ya tuvo ${plural} de estado en los últimos 15 min (${(ultimo.estadio_anterior ?? '—')} → ${ultimo.estadio_nuevo}, hace ${minutos} min).`;
      avisosCadena.push({ mensaje: msg, bloque: b });
    }
  }
  const cadenaSinConfirmar = avisosCadena.length > 0 && !confirmarCadena;

  // Medida 5 (incidente Lote 48): retroceso de estado. Con V°B° se muestra
  // como un aviso más (checkbox de confirmación); sin V°B° no se puede
  // confirmar y aplicar directo — solo armar la solicitud al ingeniero.
  const avisosRetroceso: { mensaje: string; bloque: EmpBloque }[] = [];
  if (hayCambioEstado) {
    for (const b of cambianEstadioUI) {
      if (!esRetrocesoEstado(b, estadioElegido, form.ciclo)) continue;
      const cod = (b.estadio_vfp ?? '').toUpperCase();
      const labelActual = estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.descripcion || cod;
      avisosRetroceso.push({
        mensaje: `BLK ${b.num}: retrocede de "${labelActual}" a "${estadioElegidoLabel}".`,
        bloque: b,
      });
    }
  }
  const retrocesoSinConfirmar = permVoB && avisosRetroceso.length > 0 && !confirmarRetroceso;
  const requiereSolicitudRetroceso = !permVoB && avisosRetroceso.length > 0;
  const solicitudRetrocesoLista = requiereSolicitudRetroceso && !!form.motivo_correccion.trim() && !fechaCambioFutura;

  async function solicitarRetroceso() {
    if (!solicitudRetrocesoLista) return;
    setSolicitandoRetroceso(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const bloquesAfectados = avisosRetroceso.map(a => a.bloque);
      // Una sola columna por solicitud: solo se escribe si todos entran a la fase.
      const anclaColDestino = bloquesAfectados.every(b => anclaDelCambio(estadioElegido, b.estadio_vfp))
        ? (anclaDelEstadio(estadioElegido) ?? null)
        : null;
      const resp = await fetch(buildApiUrl('/aplicaciones/estadios/retroceso-solicitudes'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          lote_id: bloquesAfectados[0]?.lote_id ?? null,
          estadio_destino: estadioElegido,
          estadio_destino_label: estadioElegidoLabel,
          ciclo_destino: form.ciclo,
          ancla_columna: anclaColDestino,
          grupo_forza: tieneGrupoForza(estadioElegido) ? (form.grupo_forza || null) : null,
          fecha_cambio: form.fecha_cambio,
          motivo: form.motivo_correccion.trim(),
          bloques: bloquesAfectados.map(b => ({
            bloque_id: b.id,
            estadio_anterior_label: estadiosCfg.find(e => e.codigo.toUpperCase() === (b.estadio_vfp ?? '').toUpperCase())?.descripcion || (b.estadio_vfp ?? ''),
          })),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || 'No se pudo enviar la solicitud');
      const noti = payload?.notificacion;
      const via = noti?.whatsapp && noti?.email ? 'WhatsApp y correo' : noti?.whatsapp ? 'WhatsApp' : noti?.email ? 'correo' : 'ningún canal (revisá la configuración)';
      setToast({ msg: `Solicitud de retroceso enviada. Se notificó al ingeniero por ${via}.`, ok: true });
      setForm(f => ({ ...f, motivo_correccion: '' }));
    } catch (err: any) {
      setToast({ msg: err?.message || 'No se pudo enviar la solicitud', ok: false });
    } finally {
      setSolicitandoRetroceso(false);
    }
  }

  const solicitudLista = !permVoB && puedeCorregirAncla && !!estadioCodigoSolicitud
    && !!form.fecha_ancla && !!form.motivo_correccion.trim() && !cronologiaSinConfirmar;

  async function solicitarCorreccion() {
    if (!solicitudLista || !anclaDelEstadoActual || !estadioCodigoSolicitud) return;
    setSolicitando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/estadios/ancla-solicitudes'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          lote_id: bloquesSinCambioUI[0]?.lote_id ?? null,
          ancla_columna: anclaDelEstadoActual,
          estadio_codigo: estadioCodigoSolicitud,
          fecha_nueva: form.fecha_ancla,
          motivo: form.motivo_correccion.trim(),
          bloque_ids: bloquesSinCambioUI.map(b => b.id),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || 'No se pudo enviar la solicitud');
      const noti = payload?.notificacion;
      const via = noti?.whatsapp && noti?.email ? 'WhatsApp y correo' : noti?.whatsapp ? 'WhatsApp' : noti?.email ? 'correo' : 'ningún canal (revisá la configuración)';
      setToast({ msg: `Solicitud enviada. Se notificó al ingeniero por ${via}.`, ok: true });
      setForm(f => ({ ...f, fecha_ancla: '', motivo_correccion: '' }));
    } catch (err: any) {
      setToast({ msg: err?.message || 'No se pudo enviar la solicitud', ok: false });
    } finally {
      setSolicitando(false);
    }
  }

  // El Grupo de Forza solo se ofrece si el estadio resultante es Post Forza.
  const mostrarGrupoForza = estadioElegido
    ? tieneGrupoForza(estadioElegido)
    : bloques.some(b => seleccionados.has(b.id) && tieneGrupoForza(b.estadio_vfp));

  return (
    <div className={panelAbierto ? "w-64 shrink-0 flex flex-col overflow-hidden" : "w-9 shrink-0 flex flex-col overflow-hidden"}
      style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
      {!panelAbierto && (
        // Colapsado: libera el ancho para una vista más analítica de la tabla
        // cuando no hay nada que editar. Se abre solo al marcar un bloque
        // (ver efecto sobre panelAbierto) o a mano con este botón.
        <button
          type="button"
          onClick={() => setPanelAbierto(true)}
          title="Mostrar panel de cambios"
          className="flex-1 w-full flex flex-col items-center justify-center gap-2 py-4 transition-opacity hover:opacity-80"
          style={{ color: n > 0 ? 'var(--emp-accent-txt)' : 'var(--ink-faint)' }}>
          <ChevronLeft size={14} />
          {n > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
              {n}
            </span>
          )}
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ writingMode: 'vertical-rl' }}>
            Cambios
          </span>
        </button>
      )}

      {panelAbierto && (
      <>
      {/* Header panel */}
      <div className="px-3 py-2.5 shrink-0 flex items-center gap-2"
        style={{ borderBottom: '1px solid var(--line-dim)' }}>
        <CheckSquare size={13} style={{ color: 'var(--emp-accent-txt)' }} />
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink)' }}>
          Cambios
        </span>
        {n > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
            {n}
          </span>
        )}
        <button
          type="button"
          onClick={() => setPanelAbierto(false)}
          title="Ocultar panel de cambios"
          className="ml-auto p-1 rounded-md transition-opacity hover:opacity-70"
          style={{ color: 'var(--ink-faint)' }}>
          <ChevronRight size={14} />
        </button>
      </div>

      {n === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 opacity-40 px-4 text-center">
          <CheckSquare size={28} style={{ color: 'var(--ink-faint)' }} />
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
            Marcá bloques en la tabla para editar su estado
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 space-y-3">

          {/* Resumen selección */}
          <div className="rounded-lg p-2.5 text-[11px]"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
            <p className="font-semibold" style={{ color: 'var(--ink)' }}>
              {n} bloque{n > 1 ? 's' : ''} seleccionado{n > 1 ? 's' : ''}
            </p>
            {estadoActualLabel && (
              <p className="mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                Estado actual: <span style={{ color: (VFP_COLOR[codVfpActual] || VFP_COLOR_FALLBACK).text }}>{estadoActualLabel}</span>
              </p>
            )}
            {n > 1 && (
              <p className="mt-0.5 italic" style={{ color: 'var(--ink-muted)' }}>
                Solo se aplican los campos que completes
              </p>
            )}
          </div>

          {/* Estado (estadio_vfp real — lo que lee el generador de boletas) */}
          <div>
            <label className={labelCls}>Nuevo estado</label>
            <select className={selectCls} value={form.estadio_vfp}
              onChange={e => set('estadio_vfp', e.target.value)}>
              {n > 1 && <option value="">— sin cambiar —</option>}
              {estadiosCfg.map(s => (
                <option key={s.codigo} value={s.codigo.toUpperCase()}>{s.descripcion} ({s.codigo.toUpperCase()})</option>
              ))}
            </select>
          </div>

          {/* Fecha del cambio de estado: es la que queda en el historial y la que
              se copia a la fecha-ancla del nuevo estadio. Por defecto hoy, pero
              editable porque el cambio suele registrarse días después. */}
          {hayCambioEstado && (
            <div>
              <label className={labelCls}>Fecha del cambio</label>
              <input type="date" className={inputCls} value={form.fecha_cambio} max={hoyISO()}
                onChange={e => set('fecha_cambio', e.target.value)} />
              <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--ink-faint)' }}>
                {anclaDelNuevoEstado
                  ? <>Se guarda en el historial y actualiza <strong>{anclaDelNuevoEstado.replace('fecha_', '')}</strong>.</>
                  : <>Se guarda en el historial. Este estadio no mueve su fecha-ancla automáticamente.</>}
              </p>
              {form.fecha_cambio > hoyISO() && (
                <p className="text-[10px] mt-1 font-semibold" style={{ color: 'var(--danger, #dc2626)' }}>
                  ⚠ La fecha no puede ser futura.
                </p>
              )}

              <div className="mt-2">
                <label className={labelCls}>Motivo del cambio <span style={{ color: 'var(--danger, #dc2626)' }}>*</span></label>
                <textarea className={inputCls} rows={2} value={form.motivo_correccion}
                  onChange={e => set('motivo_correccion', e.target.value)}
                  placeholder="ej. se aplicó la inducción en campo, corresponde pasar a Post Forza" />
                <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--ink-faint)' }}>
                  Obligatorio: queda en el historial junto a tu usuario.
                </p>
              </div>

              {avisosCronologiaCambio.length > 0 && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosCronologiaCambio.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ {a.mensaje}{' '}
                      <button type="button" className="underline font-semibold" onClick={() => setAnalisisAbierto({ bloque: a.bloque, campo: a.campo, fecha: form.fecha_cambio })}>
                        Ver análisis
                      </button>
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarCronologia} onChange={e => setConfirmarCronologia(e.target.checked)} />
                    Confirmo que la fecha es correcta a pesar del aviso
                  </label>
                </div>
              )}

              {/* Cruce contra boletas reales: si el campo ya aplicó una etapa
                  distinta, es la señal más directa de un estado desactualizado
                  o retrocedido (así se detectó el incidente del Lote 48). */}
              {avisosBoletas.length > 0 && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosBoletas.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ BLK {a.bloque.num}: {a.mensaje}
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarBoletas} onChange={e => setConfirmarBoletas(e.target.checked)} />
                    Confirmo que el estado nuevo es correcto a pesar de la boleta ya aplicada
                  </label>
                </div>
              )}

              {/* Cambios en cadena: el mismo bloque ya se tocó hace muy poco.
                  Detecta el patrón de clics de prueba o de error del incidente
                  del Lote 48 (FRC→SRC→SPC→SRC en 90 segundos). */}
              {avisosCadena.length > 0 && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosCadena.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ BLK {a.bloque.num}: {a.mensaje}
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarCadena} onChange={e => setConfirmarCadena(e.target.checked)} />
                    Confirmo que este cambio es real, no una prueba ni un error
                  </label>
                </div>
              )}

              {/* Medida 5 (incidente Lote 48): el estado nuevo queda ANTES del
                  actual en la secuencia del cultivo. Con V°B° es un aviso más
                  a confirmar; sin V°B° no se puede aplicar directo — abajo
                  aparece el panel para solicitar autorización al ingeniero. */}
              {avisosRetroceso.length > 0 && permVoB && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosRetroceso.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ {a.mensaje}
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarRetroceso} onChange={e => setConfirmarRetroceso(e.target.checked)} />
                    Confirmo que este retroceso de estado es real y está autorizado
                  </label>
                </div>
              )}

              {avisosRetroceso.length > 0 && !permVoB && (
                <div className="mt-2 rounded-lg p-2.5" style={{ background: 'var(--surface-deep)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosRetroceso.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug mb-1" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ {a.mensaje}
                    </p>
                  ))}
                  <p className="text-[11px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                    Retroceder el estado requiere autorización de <strong>ingeniería (V°B°)</strong>. Completá el motivo arriba y enviá la solicitud — se le va a notificar
                    {ingenieros.length > 0 ? <> a <strong>{ingenieros.map(i => i.nombre).join(' y ')}</strong></> : ' al ingeniero'} por WhatsApp/correo; el primero que la resuelva la deja lista.
                  </p>
                  <button
                    type="button"
                    onClick={solicitarRetroceso}
                    disabled={!solicitudRetrocesoLista || solicitandoRetroceso}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-opacity"
                    style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (!solicitudRetrocesoLista || solicitandoRetroceso) ? 0.6 : 1 }}>
                    {solicitandoRetroceso ? 'Enviando…' : 'Solicitar autorización a ingeniería'}
                  </button>
                </div>
              )}
            </div>
          )}

          {puedeCorregirAncla && permVoB && (
            <div>
              <label className={labelCls}>Fecha ancla actual</label>
              <input type="date" className={inputCls} value={form.fecha_ancla}
                onChange={e => set('fecha_ancla', e.target.value)} />
              <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--ink-faint)' }}>
                {anclaDelEstadoActual
                  ? <>Corrige <strong>{anclaDelEstadoActual.replace('fecha_', '')}</strong> en {bloquesSinCambioUI.length > 1 ? `los ${bloquesSinCambioUI.length} bloques` : 'el bloque'}, sin cambiar el estado.</>
                  : <>Corrige la fecha-ancla de cada bloque según su fase actual, sin cambiar el estado.</>}
              </p>

              {huboCorreccionAncla && (
                <div className="mt-2">
                  <label className={labelCls}>Motivo de la corrección <span style={{ color: 'var(--danger, #dc2626)' }}>*</span></label>
                  <textarea className={inputCls} rows={2} value={form.motivo_correccion}
                    onChange={e => set('motivo_correccion', e.target.value)}
                    placeholder="ej. se digitó mal la fecha, corrige a la real de campo" />
                  <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--ink-faint)' }}>
                    Obligatorio: queda en el historial junto a tu usuario y la fecha anterior.
                  </p>
                </div>
              )}

              {avisosCronologiaCorreccion.length > 0 && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosCronologiaCorreccion.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ {a.mensaje}{' '}
                      <button type="button" className="underline font-semibold" onClick={() => setAnalisisAbierto({ bloque: a.bloque, campo: a.campo, fecha: form.fecha_ancla })}>
                        Ver análisis
                      </button>
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarCronologia} onChange={e => setConfirmarCronologia(e.target.checked)} />
                    Confirmo que la fecha es correcta a pesar del aviso
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Sin V°B°: en vez de aplicar directo, se arma una SOLICITUD que le
              llega al ingeniero por WhatsApp/email con un link — la aprueba
              desde el celular, sin entrar al ERP. Igual patrón que Bodega para
              cambios de fórmula. */}
          {puedeCorregirAncla && !permVoB && (
            <div className="rounded-lg p-2.5"
              style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
              <p className="text-[11px] mb-2" style={{ color: 'var(--ink-muted)' }}>
                ¿La fecha-ancla está mal? Corregirla requiere autorización de <strong>ingeniería (V°B°)</strong> porque
                mueve el calendario de boletas y muestreos. Armá la solicitud acá — se le va a notificar al ingeniero.
              </p>

              <label className={labelCls}>Fecha correcta</label>
              <input type="date" className={inputCls} value={form.fecha_ancla}
                onChange={e => set('fecha_ancla', e.target.value)} />

              {estadiosSinCambioUnicos.size > 1 && (
                <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                  Los bloques seleccionados están en fases distintas. Seleccioná solo bloques de la misma fase para solicitar la corrección.
                </p>
              )}

              {form.fecha_ancla && estadioCodigoSolicitud && (
                <div className="mt-2">
                  <label className={labelCls}>Motivo de la corrección <span style={{ color: 'var(--danger, #dc2626)' }}>*</span></label>
                  <textarea className={inputCls} rows={2} value={form.motivo_correccion}
                    onChange={e => set('motivo_correccion', e.target.value)}
                    placeholder="ej. se digitó mal la fecha, corrige a la real de campo" />
                  <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--ink-faint)' }}>
                    Se envía junto con {bloquesSinCambioUI.length > 1 ? `los ${bloquesSinCambioUI.length} bloques` : 'el bloque'}
                    {ingenieros.length > 0 ? <> a <strong>{ingenieros.map(i => i.nombre).join(' y ')}</strong></> : ' al ingeniero'} — el primero que la resuelva la deja lista.
                  </p>
                </div>
              )}

              {avisosCronologiaCorreccion.length > 0 && (
                <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid var(--danger, #dc2626)' }}>
                  {avisosCronologiaCorreccion.map((a, i) => (
                    <p key={i} className="text-[10px] leading-snug" style={{ color: 'var(--danger, #dc2626)' }}>
                      ⚠ {a.mensaje}{' '}
                      <button type="button" className="underline font-semibold" onClick={() => setAnalisisAbierto({ bloque: a.bloque, campo: a.campo, fecha: form.fecha_ancla })}>
                        Ver análisis
                      </button>
                    </p>
                  ))}
                  <label className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ink)' }}>
                    <input type="checkbox" checked={confirmarCronologia} onChange={e => setConfirmarCronologia(e.target.checked)} />
                    Confirmo que la fecha es correcta a pesar del aviso
                  </label>
                </div>
              )}

              {form.fecha_ancla && estadioCodigoSolicitud && (
                <button
                  onClick={solicitarCorreccion}
                  disabled={!solicitudLista || solicitando}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-opacity"
                  style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (!solicitudLista || solicitando) ? 0.6 : 1 }}>
                  {solicitando ? 'Enviando…' : 'Solicitar corrección a ingeniería'}
                </button>
              )}
            </div>
          )}

          {/* Cosecha */}
          <div>
            <label className={labelCls}>Cosecha</label>
            <select className={selectCls} value={form.ciclo}
              onChange={e => set('ciclo', e.target.value as CicloBloque)}>
              <option value="PC">PC — Primera</option>
              <option value="RC">RC — Segunda</option>
              <option value="RC2">RC2 — Tercera</option>
            </select>
          </div>

          {/* Grupo forza: solo aplica a Post Forza (FPC/FRC) */}
          {mostrarGrupoForza && (
            <div>
              <label className={labelCls}>Grupo Forza</label>
              <input className={inputCls} value={form.grupo_forza}
                onChange={e => set('grupo_forza', e.target.value)}
                placeholder="ej. 18-26" />
            </div>
          )}

          <p className="text-[10px] leading-snug pt-1" style={{ color: 'var(--ink-faint)' }}>
            Las fechas se actualizan con cada cambio de estado. También podés
            corregir el ancla actual (con motivo) y consultarla en <strong>Historial</strong>.
          </p>
        </div>
      )}

      {/* Botón Aplicar */}
      {n > 0 && (
        <div className="p-3 shrink-0" style={{ borderTop: '1px solid var(--line-dim)' }}>
          {motivoFaltante && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Falta el motivo {hayCambioEstado ? 'del cambio de estado' : 'de la corrección de fecha'}.
            </p>
          )}
          {fechaCambioFutura && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              La fecha del cambio no puede ser futura.
            </p>
          )}
          {cronologiaSinConfirmar && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Confirmá el aviso de cronología para continuar.
            </p>
          )}
          {boletasSinConfirmar && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Confirmá el aviso de boletas ya aplicadas para continuar.
            </p>
          )}
          {cadenaSinConfirmar && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Confirmá el aviso de cambios en cadena para continuar.
            </p>
          )}
          {retrocesoSinConfirmar && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Confirmá el aviso de retroceso de estado para continuar.
            </p>
          )}
          {requiereSolicitudRetroceso && (
            <p className="text-[10px] mb-1.5" style={{ color: 'var(--danger, #dc2626)' }}>
              Este cambio retrocede el estado: requiere V°B° de ingeniería. Armá la solicitud más abajo.
            </p>
          )}
          <button
            onClick={aplicar}
            disabled={aplicando || motivoFaltante || fechaCambioFutura || cronologiaSinConfirmar || boletasSinConfirmar || cadenaSinConfirmar || retrocesoSinConfirmar || requiereSolicitudRetroceso}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-opacity"
            style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (aplicando || motivoFaltante || fechaCambioFutura || cronologiaSinConfirmar || boletasSinConfirmar || cadenaSinConfirmar || retrocesoSinConfirmar || requiereSolicitudRetroceso) ? 0.6 : 1 }}>
            <Save size={14} />
            {aplicando ? 'Aplicando…' : `Aplicar a ${n} bloque${n > 1 ? 's' : ''}`}
          </button>
        </div>
      )}
      </>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}
      {analisisAbierto && (
        <ModalAnalisisCronologia
          bloque={analisisAbierto.bloque}
          campo={analisisAbierto.campo}
          nuevaFecha={analisisAbierto.fecha}
          numBloque={analisisAbierto.bloque.num}
          onClose={() => setAnalisisAbierto(null)}
        />
      )}
    </div>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export function EstadiosPage({ usuarioNombre }: { usuarioNombre?: string } = {}) {
  const empresaId = useEmpresaId();
  const { perm_vob } = useAppPermisos();

  const [fincas, setFincas]                 = useState<EmpFinca[]>([]);
  const [lotes, setLotes]                   = useState<EmpLote[]>([]);
  const [bloques, setBloques]               = useState<EmpBloque[]>([]);
  const [estadiosCfg, setEstadiosCfg]       = useState<EstadioCfg[]>([]);
  const [loading, setLoading]               = useState(true);
  const [fincaSeleccionada, setFincaSel]    = useState<EmpFinca | null>(null);
  const [estadioFiltro, setEstadioFiltro]   = useState<string | null>(null);
  const [loteSeleccionado, setLoteSel]      = useState<EmpLote | null>(null);
  const [seleccionados, setSeleccionados]   = useState<Set<string>>(new Set());
  const [soloActivos, setSoloActivos]       = useState(true);
  const [imagenLote, setImagenLote]         = useState<EmpLote | null>(null);
  const [historialLote, setHistorialLote]   = useState<EmpLote | null>(null);
  const [resumenLote, setResumenLote]       = useState<EmpLote | null>(null);
  // Consulta de cronología de un bloque puntual (botón en la tabla), sin
  // relación con el panel de cambios.
  const [cronologiaConsulta, setCronologiaConsulta] = useState<EmpBloque | null>(null);
  // Buscador GLOBAL de lotes (ignora finca/estadio activos)
  const [busquedaLote, setBusquedaLote]     = useState('');

  // ── Carga ─────────────────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setLoading(true);
    // Optimización: emp_bloques tiene ~11k filas pero solo ~3k tienen estadio_vfp.
    // Filtramos en BD para reducir payload. Las columnas innecesarias también se excluyen
    // (raster_*, conteo_*, geojson) — el panel solo muestra num, área, plantas, fechas.
    async function cargarBloquesPaginado(): Promise<EmpBloque[]> {
      const PAGE = 1000;
      const COLS = 'id, lote_id, tipo, num, area_ha, area_gps, plant_count, densidad_objetivo, ' +
                   'fecha_siembra, fecha_induccion, fecha_forza, fecha_maduracion, fecha_cosecha, ' +
                   'fecha_segunda, fecha_barbecho, fecha_semillero, ultima_aplicacion, ' +
                   'fecha_induccion2, fecha_forza2, fecha_maduracion2, fecha_cosecha2, ' +
                   'sector, grupo_forza, ciclo, estado_ciclo, estadio_vfp, activo';
      let offset = 0;
      const todos: EmpBloque[] = [];
      while (true) {
        const { data, error } = await supabase
          .from('emp_bloques')
          .select(COLS)
          .eq('empresa_id', empresaId)
          .not('estadio_vfp', 'is', null)
          .order('num')
          .range(offset, offset + PAGE - 1);
        if (error || !data || data.length === 0) break;
        todos.push(...(data as unknown as EmpBloque[]));
        if (data.length < PAGE) break;
        offset += PAGE;
      }
      return todos;
    }

    const [{ data: fData }, { data: lData }, bDataAll, { data: eData }] = await Promise.all([
      supabase.from('emp_fincas').select('id, nombre, codigo, activo').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
      supabase.from('emp_lotes').select('id, finca_id, numero, nombre, activo, raster_png_url, area_ha').eq('empresa_id', empresaId).order('numero'),
      cargarBloquesPaginado(),
      supabase.from('app_estadios').select('codigo, descripcion, varfecha, activo').eq('empresa_id', empresaId).order('codigo'),
    ]);
    setFincas((fData ?? []) as any);
    setLotes((lData ?? []) as any);
    setBloques(bDataAll ?? []);
    setEstadiosCfg((eData ?? []).map((e: any) => ({
      codigo: String(e.codigo), descripcion: e.descripcion || '', varfecha: e.varfecha || null,
    })));
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset al cambiar finca/estadio o lote
  useEffect(() => { setLoteSel(null); setSeleccionados(new Set()); }, [fincaSeleccionada, estadioFiltro]);
  useEffect(() => { setSeleccionados(new Set()); }, [loteSeleccionado]);

  // ── Datos derivados ───────────────────────────────────────────────────────

  // Conteo de bloques por estadio (global — no depende de finca, solo de "solo activos")
  const conteosPorEstadio: Record<string, number> = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const lotesValidos = new Set(
      lotes
        .filter(l => (!soloActivos || l.activo))
        .map(l => l.id)
    );
    for (const b of bloques) {
      if (!lotesValidos.has(b.lote_id)) continue;
      const cod = (b.estadio_vfp || '').toUpperCase();
      if (!cod) continue;
      counts[cod] = (counts[cod] ?? 0) + 1;
    }
    return counts;
  }, [bloques, lotes, soloActivos]);

  // Set de lote_id que tienen al menos un bloque del estadio filtrado
  const lotesIdsConEstadio: Set<string> | null = React.useMemo(() => {
    if (!estadioFiltro) return null;
    const ids = new Set<string>();
    for (const b of bloques) {
      if ((b.estadio_vfp || '').toUpperCase() === estadioFiltro) ids.add(b.lote_id);
    }
    return ids;
  }, [bloques, estadioFiltro]);

  // Lotes a mostrar en el panel izquierdo:
  // - Si hay finca seleccionada: lotes de esa finca (filtrados por estadio si aplica)
  // - Si NO hay finca pero SÍ estadio: todos los lotes (de todas las fincas) con ese estadio
  // - Si no hay ninguno de los dos ("Todos"): TODOS los lotes del catálogo, ordenados.
  // Orden: por número de lote ascendente (numérico).
  const ordenarLotes = (arr: EmpLote[]) =>
    [...arr].sort((a, b) =>
      String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true, sensitivity: 'base' }));

  const lotesDeUnaFinca = (() => {
    if (fincaSeleccionada) {
      return ordenarLotes(lotes
        .filter(l => l.finca_id === fincaSeleccionada.id && (!soloActivos || l.activo))
        .filter(l => !lotesIdsConEstadio || lotesIdsConEstadio.has(l.id)));
    }
    if (estadioFiltro && lotesIdsConEstadio) {
      return ordenarLotes(lotes
        .filter(l => (!soloActivos || l.activo))
        .filter(l => lotesIdsConEstadio.has(l.id)));
    }
    // "Todos" sin finca ni estadio: listar todos los lotes del catálogo.
    return ordenarLotes(lotes.filter(l => !soloActivos || l.activo));
  })();

  // Búsqueda GLOBAL de lotes: cuando hay texto, ignora finca/estadio y busca en
  // todo el catálogo por número o nombre. Sin texto, usa la lista normal.
  const normLote = (s: unknown) =>
    String(s ?? '').toLowerCase().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
  const qLote = normLote(busquedaLote.trim());
  const lotesParaPanel = qLote
    ? lotes
        .filter(l => !soloActivos || l.activo)
        .filter(l => normLote(l.numero).includes(qLote) || normLote(l.nombre).includes(qLote))
    : lotesDeUnaFinca;

  // Mapa rápido lote_id → lote (usado para ordenar/mostrar la columna Lote)
  const loteById = React.useMemo(() => {
    const m = new Map<string, EmpLote>();
    for (const l of lotes) m.set(l.id, l);
    return m;
  }, [lotes]);

  // Bloques (sin ordenar) mostrados en panel derecho:
  // - Si hay lote seleccionado → bloques del lote (filtrados por estadio si aplica)
  // - Si hay estadio activo sin lote → todos los bloques con ese estadio (estilo VFP)
  // - Si solo hay finca activa → todos los bloques de los lotes de esa finca
  // - Si no hay nada → vacío
  const bloquesFiltrados: EmpBloque[] = (() => {
    if (loteSeleccionado) {
      return bloques
        .filter(b => b.lote_id === loteSeleccionado.id)
        .filter(b => !estadioFiltro || (b.estadio_vfp || '').toUpperCase() === estadioFiltro);
    }
    if (estadioFiltro) {
      // Vista "todos los bloques del estadio" (multi-lote, multi-finca o por finca si está seleccionada)
      const lotesValidosIds = new Set(
        lotes
          .filter(l => (!soloActivos || l.activo))
          .filter(l => !fincaSeleccionada || l.finca_id === fincaSeleccionada.id)
          .map(l => l.id)
      );
      return bloques.filter(b =>
        lotesValidosIds.has(b.lote_id) &&
        (b.estadio_vfp || '').toUpperCase() === estadioFiltro
      );
    }
    return [];
  })();

  // Ordenamiento clickeable de la tabla (estándar MYA). Default: bloque ascendente
  // (numérico, no alfabético — antes salía 1,10,11,...,2).
  type BloqueSortKey =
    | 'lote' | 'sector' | 'num' | 'estadio' | 'plantas'
    | 'area_siem' | 'area_gps' | 'densidad' | 'grupo_forza'
    | 'siembra' | 'dias' | 'inicio_fase' | 'dias_fase';
  const sort = useTableSort<BloqueSortKey>({ key: 'num', direction: 'asc' });

  const bloquesDelLote: EmpBloque[] = React.useMemo(() => {
    return sortRows(bloquesFiltrados, sort.value, (b, k): string | number | Date | null | undefined => {
      switch (k) {
        case 'lote':        return loteById.get(b.lote_id)?.numero ?? null;
        case 'sector':      return b.sector ?? null;
        case 'num':         return Number(b.num);
        case 'estadio':     return (b.estadio_vfp || '').toUpperCase();
        case 'plantas':     return b.plant_count ?? null;
        case 'area_siem':   return b.area_ha != null ? Number(b.area_ha) : null;
        case 'area_gps':    return b.area_gps != null ? Number(b.area_gps) : null;
        case 'densidad':    return b.densidad_objetivo ?? null;
        // Ordena por lo que se ve: fuera de Post Forza la celda va vacía
        case 'grupo_forza': return tieneGrupoForza(b.estadio_vfp) ? (b.grupo_forza ?? null) : null;
        case 'siembra':     return b.fecha_siembra ?? null;
        case 'dias':        return b.fecha_siembra ? Math.floor((Date.now() - new Date(b.fecha_siembra + 'T00:00:00').getTime()) / 86_400_000) : null;
        case 'inicio_fase': return fechaInicioFase(b, estadiosCfg);
        case 'dias_fase':   return diasDesde(fechaInicioFase(b, estadiosCfg));
        default:            return null;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloquesFiltrados, sort.value, loteById]);

  // Modo "vista por estadio" (cuando no hay lote pero sí estadio): mostrar columna Lote y sin selección
  const modoVistaEstadio = !loteSeleccionado && !!estadioFiltro;

  const totalPlantas  = bloquesDelLote.reduce((s, b) => s + (b.plant_count ?? 0), 0);
  const totalAreaSiem = bloquesDelLote.reduce((s, b) => s + (Number(b.area_ha) ?? 0), 0);
  const totalAreaGps  = bloquesDelLote.reduce((s, b) => s + (Number(b.area_gps) ?? 0), 0);
  const densMedia     = bloquesDelLote.length > 0
    ? Math.round(bloquesDelLote.reduce((s, b) => s + (b.densidad_objetivo ?? 0), 0) / bloquesDelLote.length)
    : 0;

  // ── Selección bloques ─────────────────────────────────────────────────────

  function toggleBloque(id: string) {
    setSeleccionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodos() {
    if (seleccionados.size === bloquesDelLote.length) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(bloquesDelLote.map(b => b.id)));
    }
  }

  const todosSeleccionados = bloquesDelLote.length > 0 && seleccionados.size === bloquesDelLote.length;
  const algunoSeleccionado = seleccionados.size > 0 && !todosSeleccionados;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header con fincas ──────────────────────────────────────────────── */}
      <div className="px-4 py-3 shrink-0 flex flex-col gap-2"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>

        <div className="flex items-center gap-4 flex-wrap">
          {/* Título */}
          <div className="shrink-0">
            <h1 className={pageTitleCls}>Estadios de Producción</h1>
            <p className={pageSubtitleCls}>MD2 Golden — cambio masivo de estado por bloque</p>
          </div>

          {/* Separador */}
          <div className="w-px h-8 shrink-0" style={{ background: 'var(--line)' }} />

          {/* Chips de fincas */}
          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            {loading ? (
              <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'var(--emp-accent)' }} />
            ) : fincas.map(f => {
              const active = fincaSeleccionada?.id === f.id;
              const nLotes = lotes.filter(l => l.finca_id === f.id).length;
              return (
                <button key={f.id}
                  onClick={() => setFincaSel(active ? null : f)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: active ? 'var(--emp-accent)' : 'var(--surface-deep)',
                    color: active ? 'var(--emp-accent-on)' : 'var(--ink)',
                    border: `1px solid ${active ? 'var(--emp-accent)' : 'var(--line)'}`,
                  }}>
                  <Leaf size={11} />
                  {f.nombre}
                  <span className="opacity-60 font-normal">{nLotes}</span>
                </button>
              );
            })}
          </div>

          {/* Controles */}
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--ink-muted)' }}>
              <input type="checkbox" checked={soloActivos} onChange={e => setSoloActivos(e.target.checked)} />
              Solo activos
            </label>
            <button onClick={cargar} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
              style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Actualizar
            </button>
          </div>
        </div>

        {/* ── Fila chips de estadios ─────────────────────────────────────── */}
        {!loading && estadiosCfg.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest shrink-0 mr-1" style={{ color: 'var(--ink-faint)' }}>
              <GitBranch size={10} /> Estadio:
            </span>
            <button
              onClick={() => setEstadioFiltro(null)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all"
              style={{
                background: estadioFiltro === null ? 'var(--emp-accent)' : 'var(--surface-deep)',
                color: estadioFiltro === null ? 'var(--emp-accent-on)' : 'var(--ink-muted)',
                border: `1px solid ${estadioFiltro === null ? 'var(--emp-accent)' : 'var(--line)'}`,
              }}>
              Todos
            </button>
            {estadiosCfg.map(est => {
              const cod = est.codigo.toUpperCase();
              const active = estadioFiltro === cod;
              const n = conteosPorEstadio[cod] ?? 0;
              return (
                <button key={cod}
                  onClick={() => setEstadioFiltro(active ? null : cod)}
                  title={est.descripcion}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all"
                  style={{
                    background: active ? 'var(--tone-info-text)' : 'var(--surface-deep)',
                    color: active ? 'var(--surface-base)' : 'var(--ink)',
                    border: `1px solid ${active ? 'var(--tone-info-text)' : 'var(--line)'}`,
                    opacity: n === 0 && !active ? 0.4 : 1,
                  }}>
                  <span className="font-mono font-bold">{cod}</span>
                  <span className="opacity-70 font-normal">{n}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Body: LOTES | BLOQUES | CAMBIOS ────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Panel LOTES */}
        <div className="w-52 shrink-0 flex flex-col overflow-hidden"
          style={{ borderRight: '1px solid var(--line)', background: 'var(--surface-base)' }}>
          <div className="px-3 py-2.5 shrink-0 flex items-center gap-2"
            style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <Layers size={13} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink)' }}>Lotes</span>
            {qLote ? (
              <span className="ml-auto text-[10px] font-medium" style={{ color: 'var(--tone-info-text)' }}>
                Búsqueda global
              </span>
            ) : fincaSeleccionada ? (
              <span className="ml-auto text-[10px] font-medium" style={{ color: 'var(--emp-accent-txt)' }}>
                {fincaSeleccionada.nombre}
              </span>
            ) : estadioFiltro ? (
              <span className="ml-auto text-[10px] font-medium" style={{ color: 'var(--tone-info-text)' }}>
                Todas · {estadioFiltro}
              </span>
            ) : null}
          </div>

          {/* Buscador global de lotes */}
          <div className="px-2 py-2 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <div className="relative">
              <Search size={12} style={{ color: 'var(--ink-faint)', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                value={busquedaLote}
                onChange={e => setBusquedaLote(e.target.value)}
                placeholder="Buscar lote…"
                className="w-full text-[11px] rounded outline-none"
                style={{
                  padding: '6px 26px 6px 26px',
                  background: 'var(--surface-deep)', border: '1px solid var(--line)',
                  color: 'var(--ink)',
                }}
              />
              {busquedaLote && (
                <button
                  onClick={() => setBusquedaLote('')}
                  aria-label="Limpiar"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[12px]"
                  style={{ color: 'var(--ink-muted)' }}>✕</button>
              )}
            </div>
          </div>

          {(
            <div className="flex-1 overflow-y-auto py-1">
              {/* cabecera */}
              {(() => {
                // Mostramos la finca de cada lote salvo cuando hay UNA finca seleccionada.
                const sinFinca = !fincaSeleccionada || !!qLote;
                const headers = sinFinca ? ['No.', 'Lote · Finca', 'Blk', ''] : ['No.', 'Nombre', 'Blk', ''];
                return (
                  <div className="grid px-3 pb-1 pt-0.5"
                    style={{ gridTemplateColumns: '2rem 1fr 2rem 1.5rem', borderBottom: '1px solid var(--line-dim)' }}>
                    {headers.map(h => (
                      <span key={h} className="text-[9px] font-bold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{h}</span>
                    ))}
                  </div>
                );
              })()}
              {lotesParaPanel.map(l => {
                const active = loteSeleccionado?.id === l.id;
                const nBloques = bloques.filter(b => b.lote_id === l.id).length;
                const tieneImagen = !!l.raster_png_url;
                const finca = (!fincaSeleccionada || qLote) ? fincas.find(f => f.id === l.finca_id) : null;
                return (
                  <div key={l.id}
                    className="grid px-3 py-1.5 mx-1 my-0.5 rounded items-center transition-colors cursor-pointer"
                    style={{
                      gridTemplateColumns: '2rem 1fr 2rem 1.5rem',
                      background: active ? 'var(--emp-accent-bg)' : 'transparent',
                      borderLeft: active ? '2px solid var(--emp-accent)' : '2px solid transparent',
                    }}
                    onClick={() => setLoteSel(active ? null : l)}>
                    <span className="font-mono text-[11px] font-bold"
                      style={{ color: active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}>
                      {l.numero}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[11px] truncate font-medium"
                        style={{ color: active ? 'var(--emp-accent-txt)' : l.activo ? 'var(--ink)' : 'var(--ink-muted)' }}>
                        {l.nombre}
                      </div>
                      {finca && (
                        <div className="text-[9px] truncate" style={{ color: 'var(--ink-faint)' }}>
                          {finca.nombre}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] text-right font-mono" style={{ color: 'var(--ink-muted)' }}>
                      {nBloques || '—'}
                    </span>
                    {/* Botón imagen */}
                    <button
                      onClick={e => { e.stopPropagation(); setImagenLote(l); }}
                      title={tieneImagen ? 'Ver imagen del lote' : 'Sin imagen'}
                      className="flex items-center justify-center w-5 h-5 rounded transition-colors"
                      style={{
                        color: tieneImagen ? 'var(--emp-accent-txt)' : 'var(--ink-faint)',
                        opacity: tieneImagen ? 1 : 0.35,
                      }}>
                      <ImageIcon size={11} />
                    </button>
                  </div>
                );
              })}
              {lotesParaPanel.length === 0 && (
                <p className="text-xs text-center py-6 px-3" style={{ color: 'var(--ink-faint)' }}>
                  {qLote ? `Sin lotes que coincidan con "${busquedaLote}".` : 'Sin lotes.'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Panel BLOQUES */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!loteSeleccionado && !modoVistaEstadio ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 opacity-40">
              <Calendar size={36} style={{ color: 'var(--ink-faint)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Seleccioná un lote o un estadio para ver sus bloques</p>
            </div>
          ) : (
            <>
              {/* Sub-header lote o estadio */}
              <div className="px-4 py-2 shrink-0 flex items-center justify-between gap-4"
                style={{ borderBottom: '1px solid var(--line-dim)', background: 'var(--surface-raised)' }}>
                <div className="flex items-center gap-2">
                  {loteSeleccionado ? (
                    <>
                      <span className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                        {loteSeleccionado.nombre}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                        {(fincaSeleccionada ?? fincas.find(f => f.id === loteSeleccionado.finca_id))?.nombre}
                      </span>
                    </>
                  ) : (
                    <>
                      <GitBranch size={13} style={{ color: 'var(--tone-info-text)' }} />
                      <span className="text-xs font-semibold font-mono" style={{ color: 'var(--ink)' }}>
                        {estadioFiltro}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                        {estadiosCfg.find(e => e.codigo.toUpperCase() === estadioFiltro)?.descripcion || 'Estadio'}
                      </span>
                      {fincaSeleccionada && (
                        <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                          · {fincaSeleccionada.nombre}
                        </span>
                      )}
                    </>
                  )}
                  {seleccionados.size > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
                      {seleccionados.size} marcado{seleccionados.size > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                  {loteSeleccionado && (
                    <button
                      onClick={() => setResumenLote(loteSeleccionado)}
                      title="Ver resumen agrupado del lote (por estado, fecha-ancla y grupo forza)"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                      <Layers size={10} /> Resumen
                    </button>
                  )}
                  {loteSeleccionado && (
                    <button
                      onClick={() => setHistorialLote(loteSeleccionado)}
                      title="Ver historial de cambios de estado de este lote"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                      <GitBranch size={10} /> Historial
                    </button>
                  )}
                  <span>{bloquesDelLote.length} bloques</span>
                  <span>{totalPlantas.toLocaleString('es-CR')} plantas</span>
                  <span>{totalAreaSiem.toFixed(2)} ha</span>
                  {totalAreaGps > 0 && <span>{totalAreaGps.toFixed(2)} ha GPS</span>}
                  {densMedia > 0 && <span>{densMedia.toLocaleString('es-CR')} pl/ha</span>}
                </div>
              </div>

              {/* Tabla bloques */}
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-raised)', zIndex: 1 }}>
                    <tr style={{ borderBottom: '1px solid var(--line)' }}>
                      {/* Checkbox todos */}
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox"
                          checked={todosSeleccionados}
                          ref={el => { if (el) el.indeterminate = algunoSeleccionado; }}
                          onChange={toggleTodos}
                          className="cursor-pointer"
                        />
                      </th>
                      {(() => {
                        // Columnas con su key de sort y alineación. En modo vista
                        // por estadio se anteponen Lote y Sector.
                        const cols: { label: string; key: BloqueSortKey; align: 'left' | 'right' }[] = [
                          ...(modoVistaEstadio
                            ? [
                                { label: 'Lote',   key: 'lote'   as BloqueSortKey, align: 'left'  as const },
                                { label: 'Sector', key: 'sector' as BloqueSortKey, align: 'left'  as const },
                              ]
                            : []),
                          { label: 'BLK',         key: 'num'         as BloqueSortKey, align: 'left'  as const },
                          { label: 'Estadio',     key: 'estadio'     as BloqueSortKey, align: 'left'  as const },
                          { label: 'Plantas',     key: 'plantas'     as BloqueSortKey, align: 'right' as const },
                          { label: 'Área Siem.',  key: 'area_siem'   as BloqueSortKey, align: 'right' as const },
                          { label: 'Área GPS',    key: 'area_gps'    as BloqueSortKey, align: 'right' as const },
                          { label: 'Densidad',    key: 'densidad'    as BloqueSortKey, align: 'right' as const },
                          { label: 'Grupo Forza', key: 'grupo_forza' as BloqueSortKey, align: 'left'  as const },
                          { label: 'Siembra',     key: 'siembra'     as BloqueSortKey, align: 'left'  as const },
                          // Edad de la planta: días desde la siembra
                          { label: 'Días',        key: 'dias'        as BloqueSortKey, align: 'right' as const },
                          // Fecha en que el bloque entró a su fase actual (el ancla del estadio)
                          { label: 'Inicio fase', key: 'inicio_fase' as BloqueSortKey, align: 'left'  as const },
                          // Cuánto lleva el bloque en esa fase
                          { label: 'Días fase',   key: 'dias_fase'   as BloqueSortKey, align: 'right' as const },
                        ];
                        return cols.map(c => (
                          <th key={c.key} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: 'var(--ink-muted)' }}>
                            <SortableTh sort={sort} sortKey={c.key} align={c.align}>{c.label}</SortableTh>
                          </th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {bloquesDelLote.length === 0 ? (
                      <tr>
                        <td colSpan={modoVistaEstadio ? 14 : 12} className="px-4 py-8 text-center text-xs" style={{ color: 'var(--ink-faint)' }}>
                          {modoVistaEstadio ? 'Sin bloques en este estadio.' : 'Sin bloques en este lote.'}
                        </td>
                      </tr>
                    ) : bloquesDelLote.map((b, i) => {
                      // Mostramos `estadio_vfp` (la fuente de verdad sincronizada con VFP)
                      // en lugar de `estado_ciclo` (que era un campo manual obsoleto).
                      // El código en mayúsculas (FPC, DPC, DRC, etc.) ahora coincide con
                      // el filtro de chips de arriba.
                      const codVfp = (b.estadio_vfp ?? '').toUpperCase();
                      const c = VFP_COLOR[codVfp] || VFP_COLOR_FALLBACK;
                      const labelVfp = codVfp
                        ? (estadiosCfg.find(e => e.codigo.toUpperCase() === codVfp)?.descripcion || codVfp)
                        : '—';
                      const dias = diasDesde(b.fecha_siembra);          // edad de la planta
                      const inicioFase = fechaInicioFase(b, estadiosCfg);
                      const diasFase = diasDesde(inicioFase);           // tiempo en la fase actual
                      const marcado = seleccionados.has(b.id);
                      const loteRow = modoVistaEstadio ? loteById.get(b.lote_id) : null;
                      const fincaRow = modoVistaEstadio && loteRow ? fincas.find(f => f.id === loteRow.finca_id) : null;
                      return (
                        <tr key={b.id}
                          onClick={() => toggleBloque(b.id)}
                          className="cursor-pointer transition-colors"
                          style={{
                            borderBottom: '1px solid var(--line-dim)',
                            background: marcado
                              ? 'var(--emp-accent-bg)'
                              : i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                          }}>
                          {/* Checkbox */}
                          <td className="px-3 py-2 w-8" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={marcado}
                              onChange={() => toggleBloque(b.id)}
                              className="cursor-pointer" />
                          </td>
                          {/* Lote (solo en modo vista por estadio) */}
                          {modoVistaEstadio && (
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className="font-mono font-bold text-[11px]" style={{ color: 'var(--ink)' }}>
                                {loteRow?.numero ?? '—'}
                              </span>
                            </td>
                          )}
                          {/* Sector / Finca (solo en modo vista por estadio) */}
                          {modoVistaEstadio && (
                            <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                              {fincaRow?.nombre ?? '—'}
                            </td>
                          )}
                          {/* Bloque num */}
                          <td className="px-3 py-2">
                            <span className="font-mono font-bold text-[11px]"
                              style={{ color: marcado ? 'var(--emp-accent-txt)' : 'var(--emp-accent-txt)' }}>
                              {b.num}
                            </span>
                          </td>
                          {/* Estadio (estadio_vfp: FPC/DPC/DRC/etc.) — fuente: VFP via sync.
                              Mostramos el NOMBRE COMPLETO del estadio (descripción de app_estadios),
                              no la clave corta. El color sigue derivando del código. */}
                          <td className="px-3 py-2">
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
                              title={codVfp || undefined}
                              style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
                              {labelVfp}
                            </span>
                          </td>
                          {/* Plantas */}
                          <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink)' }}>
                            {b.plant_count != null ? b.plant_count.toLocaleString('es-CR') : '—'}
                          </td>
                          {/* Área siembra */}
                          <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>
                            {b.area_ha != null ? Number(b.area_ha).toFixed(2) : '—'}
                          </td>
                          {/* Área GPS */}
                          <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>
                            {b.area_gps != null ? Number(b.area_gps).toFixed(2) : '—'}
                          </td>
                          {/* Densidad */}
                          <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>
                            {b.densidad_objetivo != null ? b.densidad_objetivo.toLocaleString('es-CR') : '—'}
                          </td>
                          {/* Grupo Forza — solo tiene sentido en Post Forza (FPC/FRC).
                              Fuera de esos estadios el dato sigue en BD pero no se muestra. */}
                          <td className="px-3 py-2 font-mono text-[10px]" style={{ color: 'var(--ink)' }}>
                            {tieneGrupoForza(codVfp) ? (b.grupo_forza ?? '—') : '—'}
                          </td>
                          {/* Fecha siembra */}
                          <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink)' }}>
                            {fmtF(b.fecha_siembra)}
                          </td>
                          {/* Días */}
                          <td className="px-3 py-2 text-right font-mono text-[11px] font-bold"
                            style={{ color: dias != null && dias > 200 ? 'var(--tone-warn-text)' : 'var(--ink-muted)' }}>
                            {dias ?? '—'}
                          </td>
                          {/* Inicio de fase: el ancla del estadio actual del bloque.
                              Es la fecha que se registra al cambiar de estado. */}
                          <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink)' }}>
                            <button
                              type="button"
                              className="hover:underline"
                              title="Ver cronología completa del bloque"
                              onClick={e => { e.stopPropagation(); setCronologiaConsulta(b); }}>
                              {fmtF(inicioFase)}
                            </button>
                          </td>
                          {/* Días en la fase actual (vs. "Días", que es la edad desde siembra) */}
                          <td className="px-3 py-2 text-right font-mono text-[11px] font-bold"
                            style={{ color: 'var(--ink-muted)' }}>
                            {diasFase ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totales */}
                  {bloquesDelLote.length > 0 && (
                    <tfoot style={{ position: 'sticky', bottom: 0 }}>
                      <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--surface-raised)' }}>
                        <td colSpan={modoVistaEstadio ? 5 : 3} className="px-3 py-2 text-[10px] font-bold uppercase"
                          style={{ color: 'var(--ink-muted)' }}>
                          Totales {modoVistaEstadio && <span className="ml-2 normal-case font-normal" style={{ color: 'var(--ink-faint)' }}>· {bloquesDelLote.length} bloques</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[11px]" style={{ color: 'var(--ink)' }}>
                          {totalPlantas.toLocaleString('es-CR')}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[11px]" style={{ color: 'var(--ink)' }}>
                          {totalAreaSiem.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[11px]" style={{ color: 'var(--ink)' }}>
                          {totalAreaGps > 0 ? totalAreaGps.toFixed(2) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                          {densMedia > 0 ? densMedia.toLocaleString('es-CR') : '—'}
                        </td>
                        <td colSpan={5} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>

        {/* Panel CAMBIOS */}
        <PanelCambios
          seleccionados={seleccionados}
          bloques={bloquesDelLote}
          estadiosCfg={estadiosCfg}
          onAplicado={() => { cargar(); setSeleccionados(new Set()); }}
          usuarioNombre={usuarioNombre}
          permVoB={perm_vob}
        />
      </div>

      {/* Modal imagen lote */}
      {imagenLote && (
        <ModalImagenLote lote={imagenLote} onClose={() => setImagenLote(null)} />
      )}

      {/* Modal historial de estados del lote */}
      {historialLote && (
        <ModalHistorialLote
          lote={historialLote}
          bloques={bloquesDelLote}
          estadiosCfg={estadiosCfg}
          onClose={() => setHistorialLote(null)}
        />
      )}

      {/* Modal resumen agrupado del lote */}
      {resumenLote && (
        <ModalResumenLote
          lote={resumenLote}
          bloques={bloquesDelLote}
          estadiosCfg={estadiosCfg}
          onClose={() => setResumenLote(null)}
        />
      )}

      {/* Consulta de cronología de un bloque (botón en la columna "Inicio de fase") */}
      {cronologiaConsulta && (() => {
        const cod = (cronologiaConsulta.estadio_vfp ?? '').toUpperCase();
        const col = estadiosCfg.find(e => e.codigo.toUpperCase() === cod)?.varfecha || ESTADIO_ANCLA_AUTO[cod];
        const valorActual = col ? (((cronologiaConsulta as Record<string, any>)[col] as string | null) ?? undefined) : undefined;
        return (
          <ModalAnalisisCronologia
            bloque={cronologiaConsulta}
            campo={col}
            nuevaFecha={valorActual}
            numBloque={cronologiaConsulta.num}
            etiquetaResaltada="(fase actual)"
            onClose={() => setCronologiaConsulta(null)}
          />
        );
      })()}
    </div>
  );
}
